import {
  HAND_SIZE,
  POINTS,
  sideOf,
  type PasurDealResult,
  type PasurMove,
  type PasurView,
} from "@bing/engine-pasur";
import {
  Fragment,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
  type RefObject,
} from "react";
import { Avatar } from "../components/Avatar";
import { Card } from "../components/Card";
import { GameIcon } from "../components/GameIcon";
import { SuitGlyph } from "../components/SuitGlyph";
import { shortName } from "../components/profile-ui";
import { pasurSeatEdge, ThrownCard, type SeatEdge } from "../game/card-motion";
import { DealerMarker } from "../game/dealer-marker";
import { OWN_AVATAR } from "../game/felt-geometry";
import {
  HAND_CARD_SCALE,
  HAND_FAN_BOX_H,
  handCardPlacement,
  handFanLayout,
} from "../game/hand-fan";
import { sortHand } from "../game/hand-sort";
import { LIFTED_EXTRA_PX, LIFTED_TRANSITION, useLiftToPlay } from "../game/lift-to-play";
import { TurnTimerSlot, type TableTurnTimer } from "../game/online-lifecycle";
import {
  derivePicker,
  feltPlays,
  liftableCards,
  moveFor,
  optionKey,
  optionsByCard,
  pickOnPool,
  retapOf,
  type Pick,
} from "../game/pasur-capture";
import { CLUBS_IN_A_DECK, sideBreakdown, type SideBreakdown } from "../game/pasur-breakdown";
import { dealPlan, type DealPlan } from "../game/pasur-moments";
import { POOL_CARD, poolLayout, type PoolRoom } from "../game/pasur-pool";
import { useArmedAfter } from "../game/use-armed-after";
import { useCaptureHold } from "../game/use-capture-hold";
import { usePasurDeal } from "../game/use-pasur-deal";
import { presetAvatarFor } from "../game/seat-avatar";
import {
  resolveSeatAvatar,
  resolveSeatName,
  type SeatAvatar,
  type SeatIdentity,
  type SeatPlayer,
} from "../game/seat-identity";
import { pasurSeatFallback, pasurSideWords } from "../game/seat-labels";
import { noticeForSeat, type SeatNotice } from "../game/seat-notice";
import { BANNER_PILL, NOTICE_MS, useTransientNotice } from "../game/table-notice";
import { StatusPill, TABLE_BTN, TrickStackIcon } from "../game/table-parts";
import {
  AVATAR_CORNER,
  seatNameBoxClass,
  TableSeat,
  TURN_GLOW,
  TURN_GLOW_SETTLE,
  type TableSeatProps,
} from "../game/TableSeat";
import { useCompactTable, useMediaQuery } from "../game/use-compact-table";
import { useFrameKey } from "../game/use-frame-key";
import { useCardLandSfx, useDealSfx } from "../game/use-table-sfx";
import { faDigits } from "../i18n/digits";
import { fa } from "../i18n/fa";
import { getLiftToPlay } from "../settings/prefs";

/* ------------------------------------------------------------------ *
 * پاسور's TABLE ON THE SHARED FRAME (P1 Task 3, 2026-10-10) — the frame, header, felt, seats, strip
 * and hand حکم and بی‌دل draw, for a FISHING game:
 *
 *   the frame   the 440 px column, `--game-h`, the safe-area pads (حکم's / بی‌دل's, Part B §4.1);
 *   the header  ONE 40 px row: the exit, the chat toggle's footprint IFF online, «پاسور» centred in the
 *               gap, a FIXED-width score box with the two SIDES' match totals toward ۶۲;
 *   the felt    حکم's gold-ringed felt; its corner chips — the stock's count or the final round, and
 *               who dealt — in ONE top row; the pool on it; your prompt in its bottom notices column;
 *   the seats   `TableSeat` on حکم's DIAMOND (R3): seat+1 on your RIGHT, your partner ACROSS wearing
 *               «یار», seat+3 on your LEFT — the team rings green (ما) / red (حریف), the gold turn-glow
 *               always on top; at two seats (R7: solo / dev) the one opponent across;
 *   your strip  avatar → name (the last deal under it) → [status pill → the turn timer's slot LAST];
 *   the hand    `hand-fan.ts`'s fan, `sortHand`'s order, remounted per deal and refill round.
 *
 * THE POOL AND THE CAPTURE (Task 4, R4 and card-91's refinements):
 *   the pool    `pasur-pool.ts`'s MEASURED rule in the room the felt leaves it (`usePoolRoom`: below
 *               the top seat and the corner chips, above the prompt's row, between the side seats'
 *               columns) — a grid that shrinks and overlaps as the pile grows, every index strip
 *               exposed, every tilted card inside the room; a capture re-flows the rest;
 *   the picker  `pasur-capture.ts` on the shared lift (`lift-to-play.ts`): a card with ONE legal
 *               move plays on a tap (the pref off) or lifts and previews it (the pref on: the felt,
 *               the card or «جمع کن» plays it); a card with SEVERAL capture sets lifts whatever the
 *               pref — ELIGIBLE table cards glow (`Card`'s own legal pulse: the hand's «you can
 *               play this», here «you can take this»), a tap on one CHOOSES its set (taps cycle
 *               through the sets holding it), the chosen cards ring and wear a count badge, and
 *               «جمع کن» (greyed until then) or the lifted card plays it; never hover, never
 *               auto-max, nothing chosen for the player, and a capturing card never trails.
 *
 * THE MOMENTS, ALWAYS SEEN AND HEARD (Task 5; the operator's «the last card and the full collect
 * animation always shown», حکم's and بی‌دل's 1fa3c258):
 *   a capture   the played card flies onto the set it takes, holds, and the set slides to the
 *               capturer's seat (`use-capture-hold.ts`): latched on its first render, never cut by a
 *               newer frame — the hand disabled meanwhile, the table drawn as the play found it;
 *   a سور       «سور! — {name}» in the felt's notices column as the set slides, the side's count on
 *               its seats; a cancel (D10) «سور {name} خنثی شد»; the final round's chip says no
 *               capture there makes one (D8);
 *   the scoop   at a deal's end, after its last play, the remainder slides to the last capturer with
 *               «باقی زمین به {name}» — and only then the next deal (its pool, hand and numbers).
 *
 * THE DEAL, OUT OF THE STOCK (Task 6, W14): every round of a deal is a moment too — the opening's 4 to
 * each seat in turn from the seat after the dealer, then 4 to the table; each refill's 4 to each seat —
 * every card flying out of the stock's corner chip (`use-pasur-deal.ts`), the chip counting down as
 * they leave, a buried Jack laid, seen and put back into the stock (D6b: no place in the pack shown);
 * the hand disabled and no turn named until the last card has landed. The per-deal breakdown card and
 * the live tallies came with Task 7 (below).
 * ------------------------------------------------------------------ */

/** The header's top pad — the safe area through max(env, --tg-safe): iOS zeroes env() in Bot-API-8
 *  fullscreen, and the SDK-published `--tg-safe-*` covers that case. */
const TOP_PAD = "calc(10px + max(env(safe-area-inset-top), var(--tg-safe-top, 0px)))";

/** حکم's felt, drawn the same way (`pasur-layout.test.tsx` §4 pins it against HokmTable's own). */
const FELT_STYLE: CSSProperties = {
  background:
    "radial-gradient(circle,rgb(var(--c-primary-container)) 0%,rgb(var(--c-felt-deep)) 100%)",
  boxShadow:
    "inset 0 8px 32px rgb(var(--c-felt-shadow) / .8), 0 0 0 6px rgb(var(--c-gold-deep)), 0 12px 48px rgba(0,0,0,.6)",
};

/**
 * THE CHAT TOGGLE'S FOOTPRINT — حکم's (`CHAT_TOGGLE_PX` there). `TableChat`'s closed toggle is
 * `position: fixed`, 40 × 40, 64 px in from the column's inline-start edge: the header's `px-4` (16)
 * + the exit (40) + the row's `gap-2` (8) — exactly the slot after the exit. Every ONLINE table keeps
 * it free from its first frame (`chat`), so «پاسور» centres between the box and the toggle; solo has
 * no chat, and «پاسور» centres between the box and the exit (operator's round 4, حکم's way).
 */
const CHAT_TOGGLE_PX = 40;

/**
 * THE SCORE BOX NEVER CHANGES WIDTH — حکم's rule (operator, 2026-10-03). Vazirmatn's digits are NOT
 * tabular (۳ is 12.22 px at headline-sm, ۱ 5.55, ۰ 7.95: the font's own advances), so each column is
 * a FIXED px width just over the widest thing it can hold:
 *   ما / حریف  the label (9.54 / 27.56 px at caption) over the side's total — two digits ≤ 24.44 px
 *              («۳۳»), and a total past 99 (a match can end above the target: a deal is worth 20 +
 *              5 × its سورs) ≤ 27.55 px («۱۲۳») → 28;
 *   the target «تا ۶۲» at caption, 22.2 px (a test target of 30 is 23.0) → 24.
 * So the box is one width per breakpoint — 142 px, and 118 px below 380 px where its gaps tighten —
 * whatever the numbers are. Inline px, not classes: happy-dom reads them.
 */
export const SCORE_COL = { us: 28, them: 28, target: 24 } as const;

/**
 * A SIDE SEAT'S NAME BOX — حکم's cap: `min(50vw, 220px) − 128px`, floored at 52 px (the seat box's
 * own minimum, so the column never widens past it on a narrow screen) and 92 px from 440 px up. The
 * pool keeps clear of the column it allows (`POOL_SIDE`). The top seat has the felt's width: 92 px.
 * Inline, not a Tailwind arbitrary class: `clamp()` inside a class name can compile to nothing.
 */
export const SIDE_NAME_MAX = "clamp(52px, calc(min(50vw, 220px) - 128px), 92px)";
export const TOP_NAME_MAX = "92px";

/** Where a seat sits — حکم's places, byte for byte: the seat across at `top-[38px]` (its «یار» tag
 *  floats 3 px over the avatar and clears the felt's gold ring and the header band, as حکم's «حاکم»
 *  chip does), the side seats centred, 16 px past the safe-area inset. */
const SEAT_POS: Record<Exclude<SeatEdge, "bottom">, string> = {
  top: "top-[38px] left-1/2 -translate-x-1/2",
  right:
    "top-[50%] right-[calc(max(env(safe-area-inset-right),var(--tg-safe-right,0px))_+_16px)] -translate-y-1/2",
  left: "top-[50%] left-[calc(max(env(safe-area-inset-left),var(--tg-safe-left,0px))_+_16px)] -translate-y-1/2",
};

/**
 * The pool keeps clear of the side seats: a side seat's column starts 16 px past the inset (4 px
 * inside the felt, which is inset 12) and is at most `SIDE_NAME_MAX` wide (it is never narrower than
 * its 52 px), so the pool starts 2 px past it. Custom properties, used through `var()`: happy-dom's
 * parser drops a `clamp()` from `left` / `right` but keeps a custom property (felt-geometry's note).
 */
const POOL_SIDE = {
  "--pool-l": `calc(max(env(safe-area-inset-left), var(--tg-safe-left, 0px)) + ${SIDE_NAME_MAX} + 6px)`,
  "--pool-r": `calc(max(env(safe-area-inset-right), var(--tg-safe-right, 0px)) + ${SIDE_NAME_MAX} + 6px)`,
  left: "var(--pool-l)",
  right: "var(--pool-r)",
} as CSSProperties;

/** The team tones — حکم's (`teamRing` / `teamTagClass` there; §5 pins them against HokmTable's
 *  rendered seats): your side green, the other side red. Off turn only — `TableSeat`'s gold
 *  turn-glow always wins. */
const teamRing = (mine: boolean): string =>
  mine
    ? "0 0 0 2px rgb(var(--c-primary)), 0 4px 8px rgba(0,0,0,.4)"
    : "0 0 0 2px rgb(var(--c-danger-bluff)), 0 4px 8px rgba(0,0,0,.4)";
/** A seat's name box SKIN (`TableSeat` draws its frame and its truncation): حکم's caption-sized box
 *  in its side's colour. */
const seatNameSkin =
  (mine: boolean) =>
  (compactPad: string | null): string =>
    `${compactPad ?? "px-2"} py-0.5 font-caption text-caption shadow-md ${
      mine
        ? "border-primary/70 bg-surface-container/90 text-primary"
        : "border-error/70 bg-surface-container/90 text-error"
    }`;
/** Your own ring off turn: you anchor your side — green, as حکم's. */
const OWN_RING = "0 0 0 2px rgb(var(--c-primary))";

/**
 * THE FELT'S BOTTOM ROW — your prompt and the picker's «جمع کن» wear ONE pill (one height, so the
 * row never changes height and the pool's room never moves with the turn); an invisible strut holds
 * that height on every screen and every turn. Below 360 px the pill drops to the caption size (the
 * prompt's rule since Task 3). The pill is TWO class lists: what gives it its HEIGHT (the type, the
 * vertical padding — the strut wears exactly these) and what gives it its WIDTH and look. ⚠️ Never
 * «the pill plus `px-0`»: Tailwind resolves two utilities of one property by STYLESHEET order, not
 * class order, so `px-3.5` beats a `px-0` written after it — a strut built that way is 28 px of
 * padding that shifts the row off centre, and happy-dom cannot see it.
 */
const FELT_PILL_TYPE =
  "m-0 py-1 font-label-md text-label-md max-[359px]:text-caption max-[359px]:font-bold";
const FELT_PILL_BOX =
  "max-w-full rounded-full border px-3.5 text-center shadow-md max-[359px]:px-2.5";
const FELT_PILL = `${FELT_PILL_TYPE} ${FELT_PILL_BOX}`;
/** The strut: the pill's height, no width at all — and `-me-2` eats the row's `gap-2`, so the
 *  prompt and «جمع کن» centre as if it were not there. */
const FELT_STRUT = `${FELT_PILL_TYPE} invisible w-0 -me-2`;

/** The count badge on a CHOSEN pool card (card-91's (a)): how many table cards the set takes —
 *  inside the card's own box, at the PHYSICAL top-right: the face's index is at the physical top-left
 *  (and, turned, bottom-right), and a logical `end-0.5` is the LEFT in this RTL shell — it sat on the
 *  rank (Task 4 review, Important 1). Card faces are direction-agnostic art. */
const COUNT_BADGE =
  "pointer-events-none absolute right-0.5 top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-secondary px-1 font-caption text-[10px] font-black leading-none text-ink-on-gold shadow-md";

/* A CHOSEN pool card does NOT rise out of the pile: the slot the rule gives it is the ONE place the
 * measured room keeps clear of the seats, the chips, the prompt and the felt's edge, and a 6 px
 * `translateY` carried a chosen card's index strip under the top seat's name (320×568 ×1.3, the
 * Task 4 run: 10 `seat-pool` failures, every one a chosen card). The ring and the count badge are
 * the strong state (R4 (a)), both inside the card's own box. */

/** The room the pool lays out in before anything is measured (happy-dom, the first paint): the
 *  smallest named phone's — 320×568 under a ×1.3 text zoom, read off the layout probe (its height
 *  exact, its width the conservative 172 of the live 180: `POOL_SIDE` insets from the felt's edge,
 *  the probe's seat boxes were read from main's) — so a test's numbers are that phone's and the
 *  first paint is never larger than it. */
const DEFAULT_POOL_ROOM: PoolRoom = { w: 172, h: 124 };
/** The pool keeps this much clear of the parts above and below it (px). */
const POOL_CLEAR = 2;

/** The pool's measured room: its insets from the felt's top and bottom, and the room's size. */
export interface PoolRoomBox {
  readonly top: number;
  readonly bottom: number;
  readonly room: PoolRoom;
}

/**
 * THE ROOM THE FELT LEAVES THE POOL — MEASURED, never a breakpoint (a WebView text zoom grows the
 * seat's name box, the chips and the prompt, and no media query can see it: Task 3's lesson). Read
 * off the laid-out boxes with `getBoundingClientRect` before the first paint (a layout effect) and
 * again whenever one of them resizes (the zoom, a rotation, a notice replacing a name, the row's
 * pill) or a late web font lands — the pattern of `useRichestFit` above and حکم's `useLaidOutSize`.
 * The top is the lower of the top seat's box (`[data-seat]`, the seat drawn across) and the corner
 * chips' row; the bottom is the felt's bottom row; the sides are `POOL_SIDE`'s (the box's own
 * width). Unlaid-out (happy-dom), every box is 0 and the room stays null — the caller falls back to
 * `DEFAULT_POOL_ROOM`. The pool's own contents never move the boxes it reads, so a read cannot feed
 * back into the next.
 */
function usePoolRoom(
  felt: RefObject<HTMLDivElement>,
  box: RefObject<HTMLDivElement>,
  corners: RefObject<HTMLDivElement>,
  bottomRow: RefObject<HTMLDivElement>,
  topSeat: number | null,
  key: string,
): PoolRoomBox | null {
  const [room, setRoom] = useState<PoolRoomBox | null>(null);
  useLayoutEffect(() => {
    const feltEl = felt.current;
    const boxEl = box.current;
    if (!feltEl || !boxEl) return undefined;
    const seatEl =
      topSeat === null
        ? null
        : (feltEl.parentElement?.querySelector<HTMLElement>(`[data-seat="${topSeat}"]`) ?? null);
    const read = (): void => {
      const f = feltEl.getBoundingClientRect();
      if (!(f.height > 0 && f.width > 0)) return;
      const under = (el: HTMLElement | null): number =>
        el ? el.getBoundingClientRect().bottom - f.top : 0;
      const top = Math.max(under(seatEl), under(corners.current)) + POOL_CLEAR;
      const row = bottomRow.current ? bottomRow.current.getBoundingClientRect() : null;
      const bottom = (row ? f.bottom - row.top : 0) + POOL_CLEAR;
      const next: PoolRoomBox = {
        top,
        bottom,
        room: { w: boxEl.getBoundingClientRect().width, h: f.height - top - bottom },
      };
      setRoom((prev) =>
        prev &&
        prev.top === next.top &&
        prev.bottom === next.bottom &&
        prev.room.w === next.room.w &&
        prev.room.h === next.room.h
          ? prev
          : next,
      );
    };
    read();
    const fonts: EventTarget | undefined = document.fonts;
    fonts?.addEventListener("loadingdone", read);
    const ro = typeof window.ResizeObserver === "function" ? new window.ResizeObserver(read) : null;
    for (const el of [feltEl, seatEl, corners.current, bottomRow.current]) if (el) ro?.observe(el);
    return () => {
      ro?.disconnect();
      fonts?.removeEventListener("loadingdone", read);
    };
  }, [felt, box, corners, bottomRow, topSeat, key]);
  return room;
}

/** Two measured rooms the same (a re-measure that found nothing new keeps its object)? */
function sameRoom(a: PoolRoomBox | null, b: PoolRoomBox | null): boolean {
  return (
    a === b ||
    (a !== null &&
      b !== null &&
      a.top === b.top &&
      a.bottom === b.bottom &&
      a.room.w === b.room.w &&
      a.room.h === b.room.h)
  );
}

/** Does room `a` hold all of room `b` — no edge of it moved in? */
function holdsRoom(a: PoolRoomBox, b: PoolRoomBox): boolean {
  return a.top <= b.top && a.bottom <= b.bottom && a.room.w >= b.room.w && a.room.h >= b.room.h;
}

/**
 * WHICH ROOM THE POOL IS LAID IN NOW — `useLaidRoom`'s decision, pure (its comment has the rule): the
 * room laid so far, or the one just measured. A room that GREW is held for the pool's next change —
 * unless the pool OVERFLOWS the room it lies in (B3 item 6, 2026-10-20): a resize that passes
 * through a degenerate layout (a Desktop window, a WebView's viewport settling, a walk's size steps)
 * measures a room the pile cannot fit (`fits: false`: the floor scale, centred, past the room's
 * top), and holding that one until the pool changed left the cards under the top seat's name and
 * avatar at 320×492@1.5 and 384×569@1.5 in the live walks, where a fresh load at either size is
 * clean. Such a pile re-flows into the grown room at once.
 */
export function nextLaidRoom(
  measured: PoolRoomBox | null,
  laid: PoolRoomBox | null,
  at: { holding: boolean; armed: boolean; poolChanged: boolean; poolSize: number },
): PoolRoomBox | null {
  if (at.holding || sameRoom(measured, laid)) return laid;
  const grew = laid !== null && measured !== null && holdsRoom(measured, laid);
  const overflows = laid !== null && !poolLayout(at.poolSize, laid.room).fits;
  return !grew || !at.armed || at.poolChanged || at.poolSize === 0 || overflows ? measured : laid;
}

/**
 * THE ROOM THE POOL IS LAID OUT IN — the measured room (`usePoolRoom`), handed to the rule so that a
 * pool card slides only when the POOL changes (a capture re-flows the rest, a card joins it), never
 * because the room around it moved (Task 5 review, Minor 2 — fix round 1, F2c):
 *   a moment on screen   the room holds still: a banner or a سور chip that appears at the reveal
 *                        re-measures it, and the cards still sliding away must not re-flow under
 *                        their own collect; the room is taken at the release;
 *   a room that SHRANK   is taken at once — a pool card never lies under a seat, a chip or a banner
 *                        (a banner appears at a reveal, so that is a release, where the pool changes);
 *   a room that GREW     — a banner expiring NOTICE_MS after its reveal (the scoop's label over the
 *                        next deal's first cards), a notice leaving a seat — waits for the pool's next
 *                        change, which re-flows it anyway; at once when the pool is empty, and while
 *                        the slots' transition is off (the first measurement, a new deal's pool):
 *                        nothing can slide then.
 * Its own state, set in render as the hold's is (never a ref written in render), with the slots'
 * transition (`useArmedAfter`): armed two animation frames after the room is laid, again per deal.
 */
function useLaidRoom(
  measured: PoolRoomBox | null,
  holding: boolean,
  pool: readonly string[],
  dealNumber: number,
): { room: PoolRoomBox | null; armed: boolean } {
  const cards = `${dealNumber}|${pool.join(",")}`;
  const [laid, setLaid] = useState<{ room: PoolRoomBox | null; cards: string }>(() => ({
    room: measured,
    cards,
  }));
  const armed = useArmedAfter(laid.room !== null, dealNumber);
  const room = nextLaidRoom(measured, laid.room, {
    holding,
    armed,
    poolChanged: cards !== laid.cards,
    poolSize: pool.length,
  });
  if (room !== laid.room || cards !== laid.cards) setLaid({ room, cards });
  return { room, armed };
}

/** The felt's corner chips: one look (حکم's trick counter, بی‌دل's round chip). The box without its
 *  line rule: the final round's chip WRAPS (two utilities of one property resolve by stylesheet
 *  order, not class order — `whitespace-normal` after `whitespace-nowrap` would lose). */
const CORNER_CHIP_BOX =
  "m-0 flex items-center gap-1 rounded-full border border-secondary/40 bg-surface-container/85 px-2 py-0.5 font-caption text-caption shadow-lg";
const CORNER_CHIP = `${CORNER_CHIP_BOX} whitespace-nowrap`;

/**
 * THE STOCK'S CORNER NEVER REACHES THE TOP SEAT — its slot is half the chips' row less the top seat's
 * ring and the dealer's marker on it, and 4 px of air: the ring is the 40 px avatar in 2 px of
 * padding (half 22), the marker (`DealerMarker`, card-a7 B11) stands 4 px past the ring's corner on
 * this side (`AVATAR_CORNER`'s `-right-1`) — 26 px from the middle — so the slot ends 30 px from it.
 * At 24 px (the ring alone, before the marker) the final round's two lines under ×1.5 at 576×886 lay
 * 2 × 3 px on the top dealer's marker (B11's filtered run: notice-seat, two final-round states).
 * «دست آخر — سور ندارد» (Task 5, D8) is wider than the slot at 320 px under a ×1.3 text zoom and at
 * 384 under ×1.3, so it WRAPS to two lines inside the slot instead of reaching the seat; where it
 * fits (from ≈ 384 px at ×1) it is one line. The row's height then grows at its corner, which never
 * moves the pool: the room's top is the lower of the corners and the top seat, and the top seat's
 * name sits far below two lines of caption.
 */
export const CORNER_SLOT_MAX = "calc(50% - 30px)";

/**
 * A NAME IN A MOMENT'S BANNER below 412 px is cut to its first 9 characters (and «…») — بی‌دل's
 * measured rule for its Q♠ and moon lines (`MOON_NAME_NARROW` there), which a 16-character Latin name
 * would otherwise cut at the pill's `truncate`: in this RTL shell the cut falls at the line's END, the
 * physical left, where a Latin name STARTS. The name is a bidi isolate inside the line (`fa.pasur`).
 */
export const BANNER_NAME_NARROW_QUERY = "(max-width: 411.98px)";
export const BANNER_NAME_NARROW = 9;

/**
 * A MOMENT'S LINE IN EVERY FORM, richest first (Task 8a fix round 1, F1) — the player's name cut to
 * fewer and fewer of its FIRST characters, «…» after them: from `cap` (BANNER_NAME_NARROW below 412
 * px, `shortName`'s 16 above — the forms the banners had before, so every banner that fits reads as it
 * did) down to one. The top form is the banner as it was, whatever its cut ends on; below it, a cut
 * that ends on a space is left out — the next form says as much in less room.
 * The banner shows the first form its room holds, by MEASURE (`MomentBanner`) — never a CSS cut, which
 * in this RTL shell falls at the line's END, the physical left, where a Latin name STARTS.
 */
export function bannerLines(line: (name: string) => string, name: string, cap?: number): string[] {
  const chars = [...name];
  const richest = shortName(name, cap);
  const kept = [...richest].length - (richest === name ? 0 : 1);
  const forms: string[] = [];
  for (let n = kept; n >= 1; n--) {
    if (n < kept && /\s$/u.test(chars.slice(0, n).join(""))) continue;
    forms.push(line(shortName(name, n)));
  }
  return forms.length > 0 ? forms : [line(name)];
}

/** The سور chip under a seat (its side's standing count) — the corner chips' type, in gold. */
const SUR_CHIP =
  "m-0 inline-block whitespace-nowrap rounded-full border border-secondary/60 bg-surface-container/90 px-1.5 py-0.5 font-caption text-caption leading-none text-secondary shadow-md";

/** Where the card that took a set lands: on the set's first card, a quarter of a card toward the
 *  pool's middle — so the card it took still shows its index under it. */
const PLAYED_NUDGE = { x: 0.28, y: 0.22 } as const;

/** Two lines of a short word in the notices' script (`fa.pasur.us`): the copy a form's height is held
 *  against (`useRichestFit`'s `room`) — one inline box, so a flex chip keeps its break. */
const TWO_LINES = (
  <span>
    {fa.pasur.us}
    <br />
    {fa.pasur.us}
  </span>
);

/**
 * THE FINAL ROUND'S CORNER CHIP ON TWO LINES AT MOST (Task 8b) — «دست آخر — سور ندارد» wraps in its
 * slot (`CORNER_SLOT_MAX`) by design, and where it would take a THIRD line (300×667 under ×1.5: 71.5
 * px tall, into the top seat's name box) it says «دست آخر» (`finalRoundShort`). The form is picked by
 * HEIGHT against a two-line copy of the chip in the slot's width (`useRichestFit`'s `room`); the
 * copies ride in the corners row (the slot is no positioned box, so the row is their width's
 * reference, as it is the slot's), invisible, unheard — the forms out of flow, the room in it (its
 * box's height, so a text-size change mid-round reaches the box's ResizeObserver). Assistive tech
 * hears the whole chip in either form.
 */
export const FINAL_ROUND_FORMS = [fa.pasur.finalRound, fa.pasur.finalRoundShort] as const;
const FINAL_ROUND_CHIP = `${CORNER_CHIP_BOX} max-w-full whitespace-normal text-center leading-tight text-secondary`;
function FinalRoundChip(): ReactNode {
  const boxRef = useRef<HTMLDivElement>(null);
  const fit = useRichestFit(
    boxRef,
    "final-round",
    "[data-final-round-form]",
    "height",
    "[data-final-round-room]",
  );
  const short = fit >= FINAL_ROUND_FORMS.length - 1 ? 1 : 0;
  return (
    <>
      <span
        data-testid="pasur-final-round"
        data-form={short ? "short" : "whole"}
        role="img"
        aria-label={fa.pasur.finalRound}
        className={FINAL_ROUND_CHIP}
      >
        {FINAL_ROUND_FORMS[short]}
      </span>
      <div
        ref={boxRef}
        aria-hidden="true"
        className="invisible pointer-events-none absolute start-0 top-0"
        style={{ width: CORNER_SLOT_MAX }}
      >
        {/* the room IN FLOW (review M-3): the box's own height is the two lines', so a text-size
            change resizes the one box the ResizeObserver watches — the copies alone, out of flow,
            left it 0 px tall at any zoom */}
        <span data-final-round-room="" className={FINAL_ROUND_CHIP}>
          {TWO_LINES}
        </span>
        {FINAL_ROUND_FORMS.map((f) => (
          <span
            key={f}
            data-final-round-form=""
            className={`${FINAL_ROUND_CHIP} absolute start-0 top-0`}
          >
            {f}
          </span>
        ))}
      </div>
    </>
  );
}

/**
 * A SIDE SEAT'S NOTICE ON TWO LINES AT MOST (Task 8b) — a side seat's notice wraps in its box
 * (`TableSeat`'s `wrapNotice`, capped at `SIDE_NAME_MAX`: 52 px at 320 and 300 px), and under ×1.5
 * «در حال اتصال» took THREE lines there (76 px tall). Where a notice has SHORT forms (`NOTICE_SHORT`)
 * and the whole one would take a third line, the seat paints the richest short form that takes ONE
 * line (Task 8b follow-up: «اتصال…» took two at the 52 px floor, the «…» broken off alone, where
 * «اتصال» fits whole) — each picked by HEIGHT (`useRichestFit`'s `room`): the whole against a two-line
 * copy of the seat's own notice box, a short form against a one-line copy; its copies beside the seat,
 * invisible, out of flow, unheard; no short form on one line (never at a measured size): the last. The
 * notice's label (the whole sentence) is untouched, and so are `TableSeat` and every other table.
 */
export const NOTICE_SHORT: Readonly<Record<string, readonly string[]>> = {
  [fa.online.seatReconnecting]: [fa.pasur.noticeReconnecting, fa.pasur.noticeReconnectingWord],
};
/** The notice's own type — `SeatNoticeText`'s (pinned against it in `pasur-layout.test.tsx`): the
 *  copies must break where the notice does. */
export const NOTICE_TYPE = { fontSize: 12, fontWeight: 700, letterSpacing: 0 } as const;
function PasurSeat(props: TableSeatProps): ReactNode {
  const { notice = null, wrapNotice = false, compact = false, nameSkin, nameMax } = props;
  const shorts = notice !== null && wrapNotice ? NOTICE_SHORT[notice.text] : undefined;
  const key = notice !== null && shorts !== undefined ? [notice.text, ...shorts].join("\n") : null;
  const boxRef = useRef<HTMLDivElement>(null);
  // the whole notice on two lines at most — else the richest short form on one
  const whole = useRichestFit(
    boxRef,
    key,
    '[data-notice-form="whole"]',
    "height",
    '[data-notice-room="2"]',
  );
  const short = useRichestFit(
    boxRef,
    key,
    '[data-notice-form="short"]',
    "height",
    '[data-notice-room="1"]',
  );
  const shown =
    notice !== null && shorts !== undefined && whole > 0
      ? { ...notice, text: shorts[Math.min(short, shorts.length - 1)]! }
      : notice;
  const box = seatNameBoxClass(nameSkin, compact, true, true);
  return (
    <>
      <TableSeat {...props} notice={shown} />
      {notice !== null && shorts !== undefined ? (
        <div
          ref={boxRef}
          aria-hidden="true"
          className="invisible pointer-events-none absolute start-0 top-0"
          style={{ minWidth: nameMax, maxWidth: nameMax }}
        >
          <span data-notice-room="2" className={box}>
            <span style={NOTICE_TYPE}>{TWO_LINES}</span>
          </span>
          <span data-notice-room="1" className={`${box} absolute start-0 top-0 w-full`}>
            <span style={NOTICE_TYPE}>{fa.pasur.us}</span>
          </span>
          {[notice.text, ...shorts].map((f, i) => (
            <span
              key={f}
              data-notice-form={i === 0 ? "whole" : "short"}
              className={`${box} absolute start-0 top-0 w-full`}
            >
              <span style={NOTICE_TYPE}>{f}</span>
            </span>
          ))}
        </div>
      ) : null}
    </>
  );
}

/** «یار» over your partner's avatar — out of flow (`TableSeat`'s `aboveAvatar`), no wider than it. */
function PartnerTag(): ReactNode {
  return (
    <span
      className="m-0 block whitespace-nowrap rounded-full bg-primary-container px-1.5 py-0.5 leading-none text-on-primary-container shadow-md"
      style={{ fontSize: 9, fontWeight: 800 }}
    >
      {fa.pasur.partner}
    </span>
  );
}

/**
 * THE LIVE TALLIES UNDER YOUR NAME (P1 Task 7, W17 — the controller's ruling D3): each side's clubs
 * this deal and which side holds the 2♣ and the 10♦, in the richest form that fits its room WHOLE:
 *   words    «ما ♣۸ ۲♣  حریف ♣۵ ۱۰♦»  — each side's word, its clubs count, the cards it holds;
 *   numbers  «♣۸ ۲♣  ♣۵ ۱۰♦»          — the side words go first: each side's group in the header box's
 *                                       order (ما, then حریف) and colours, each part still named for
 *                                       assistive tech.
 * BOTH SIDES' CLUBS AND EVERY OWNER, IN EVERY STATE (card-a7 B8 — the operator revoked the
 * limitation): the forms that dropped them (the owners alone, the clubs alone, your side's clubs
 * alone, nothing — Task 8a / 8b, where the room beside the gold pill and the timer's slot ran short:
 * 43.2 px at 300×667 and 63.2 at 320×568 on your own move under ×1.5) are gone. Where the worst
 * `numbers` line cannot fit beside the widest pill, your strip takes TWO rows instead (`StripRows`,
 * decided per screen, never per state) and the line has the whole width under your name, the pill and
 * the timer's slot. Past the last form it stays `numbers` — the probes' `extra-cut` / `tallies-lost`
 * say so, never a blank. WHY FORMS: no ONE form is readable at every width; a viewport breakpoint
 * cannot see the text zoom, so the line MEASURES (`useRichestFit`).
 * It REPLACES the last deal's «+n» line (D3): every deal's points now show on its own card, the header
 * box carries the totals — ONE home for each number: the totals in the box, the clubs and the two
 * cards here, the سور count on the seats (Task 5's measured home — a count that changes what a clear
 * does, D10, cannot live in a line that a narrow screen drops).
 * As the felt shows it (`shown.tallies`): a capture counts at its release, never before it lands.
 */
export const TALLY_FORMS = ["words", "numbers"] as const;
type TallyForm = (typeof TALLY_FORMS)[number];
/** How many of the 2♣ and the 10♦ the sides hold between them — the line says so (`data-tallies-held`,
 *  the probes' subject for «the owners shown»). */
const cardsHeld = (tallies: PasurView["tallies"]): number =>
  tallies.reduce((n, t) => n + (t.twoOfClubs ? 1 : 0) + (t.tenOfDiamonds ? 1 : 0), 0);

/** The line's row and every form's copy: one row, its parts never wrapped, the spacing a gap. */
const TALLY_ROW = "m-0 flex items-center gap-x-2 whitespace-nowrap";

/** A suit pip at the caption's size — `SuitGlyph`, never a Unicode suit (no shipped font has one). */
function Pip({ suit }: { suit: "clubs" | "diamonds" }): ReactNode {
  return (
    <span aria-hidden="true" className="m-0 flex flex-none" style={{ fontSize: "1.05em" }}>
      <SuitGlyph suit={suit} />
    </span>
  );
}

/** پاسور's card ids name the suit by its letter (`5C`, `TH`); `SuitGlyph` by its word. */
const SUIT_WORD: Record<string, "clubs" | "diamonds" | "hearts" | "spades"> = {
  C: "clubs",
  D: "diamonds",
  H: "hearts",
  S: "spades",
};

/**
 * A TABLE CARD AS A GROUP CHIP DRAWS IT (B3 item 2): its rank, then its suit's pip — the tallies'
 * way of writing a card (`TallySide`), in the card's own ink: red for ♥ ♦, the surface's for ♠ ♣
 * (شلم's trump pip's pair on the dark felt).
 */
function SetCard({ id }: { id: string }): ReactNode {
  const suit = id.slice(-1);
  const red = suit === "H" || suit === "D";
  return (
    <span
      className={`m-0 flex flex-none items-center leading-none ${red ? "text-danger-bluff" : "text-on-surface"}`}
    >
      {fa.pasur.setRank(id.slice(0, -1))}
      <span aria-hidden="true" className="m-0 flex flex-none" style={{ fontSize: "1.05em" }}>
        <SuitGlyph suit={SUIT_WORD[suit]} />
      </span>
    </span>
  );
}

/** A measuring copy's words, drawn from its `data-copy-text` (no text node of its own). */
const COPY_TEXT = "before:content-[attr(data-copy-text)]";

/** A group chip's box: the felt pill's TYPE (so the row keeps its one height), a tighter pad — and
 *  tighter still for a chip that draws its number (the crowded form). */
const SET_CHIP_BOX = `${FELT_PILL_TYPE} flex flex-none items-center gap-1 whitespace-nowrap rounded-full border border-solid shadow-md`;
const SET_CHIP = `${SET_CHIP_BOX} px-2.5`;
const SET_CHIP_NUMBER = `${SET_CHIP_BOX} px-2`;
/** …and its tightest, `px-1` (card-a7 B6) — only where no other form holds the row in one line. */
const SET_CHIP_TIGHT = `${SET_CHIP_BOX} px-1`;

/**
 * THE ROW'S FORMS, richest first — what `useRichestFit` measures (`[data-sets-form]`): the question
 * and the chips that draw their cards; the chips alone; and, for a crowded choice (a 5 on a pool of
 * sixteen offers six sets and more), each chip its NUMBER — «۱» «۲» … — its set ringed and counted
 * on the table once it is pressed; and (card-a7 B6) the numbers TIGHT — at Telegram Desktop's
 * 256×393@1.5 the six numbers and «جمع کن» are one line only so: the felt is 121 px tall there and a
 * second line would climb onto the seats. Past the last form the row WRAPS (never scrolls).
 */
const SETS_FORMS = [
  { question: true, numbers: false, tight: false },
  { question: false, numbers: false, tight: false },
  { question: false, numbers: true, tight: false },
  { question: false, numbers: true, tight: true },
] as const;

/**
 * THE GROUPS (B3 item 2): «کدام دسته؟» (`question`) and a chip per set, in the server's order — each a
 * button (`onPick`) that chooses its set, pressed while it is the chosen one, drawing its cards or
 * (`numbers`) its number; or, with no `onPick`, the same boxes inert (the row's measuring copy).
 * `mx-auto` centres them where the row has room and lets the scroller start at their first chip
 * where it has not; `wrap` (a pointer's screen — Telegram Desktop) lets them break onto more lines,
 * centred, instead (card-a7 B6).
 */
function SetChips({
  sets,
  selectedKey,
  question,
  numbers = false,
  tight = false,
  wrap = false,
  onPick,
}: {
  sets: readonly (readonly string[])[];
  selectedKey: string | null;
  question: boolean;
  numbers?: boolean;
  tight?: boolean;
  wrap?: boolean;
  onPick: ((key: string) => void) | null;
}): ReactNode {
  return (
    <div
      className={`m-0 flex items-center ${
        wrap ? "min-w-0 flex-wrap justify-center gap-y-1" : "mx-auto flex-none"
      } ${tight ? "gap-x-0.5" : numbers ? "gap-x-1" : "gap-x-1.5"}`}
    >
      {question ? (
        <span
          // the measuring copy draws its words as generated content: the same width, no text node
          // (a test's `getByText` finds the question once)
          data-copy-text={onPick === null ? fa.pasur.pickSet : undefined}
          className={`${FELT_PILL} whitespace-nowrap border-secondary/50 bg-surface-container/90 text-secondary ${
            onPick === null ? COPY_TEXT : "animate-pulse"
          }`}
        >
          {onPick === null ? null : fa.pasur.pickSet}
        </span>
      ) : null}
      {sets.map((set, i) => {
        const key = optionKey(set);
        const look = `${tight ? SET_CHIP_TIGHT : numbers ? SET_CHIP_NUMBER : SET_CHIP} ${
          key === selectedKey
            ? "border-secondary bg-secondary/25"
            : "border-secondary/50 bg-surface-container/90"
        }`;
        const cards = numbers ? (
          <span className="m-0 leading-none text-on-surface">{faDigits(i + 1)}</span>
        ) : (
          set.map((c) => <SetCard key={c} id={c} />)
        );
        if (onPick === null)
          return (
            <span key={key} className={look}>
              {cards}
            </span>
          );
        return (
          <button
            key={key}
            type="button"
            data-set={key}
            aria-pressed={key === selectedKey}
            aria-label={fa.pasur.setOption(
              i + 1,
              set.map((c) => fa.pasur.setCard(c.slice(0, -1), c.slice(-1))),
            )}
            onClick={() => onPick(key)}
            className={`appearance-none m-0 ${look} cursor-pointer active:scale-95`}
          >
            {cards}
          </button>
        );
      })}
    </div>
  );
}

/** One side's tallies — its word unless the form drops the words, its clubs count, and the 2♣ / 10♦
 *  it holds, each a small card-like chip; ONE part that never shrinks or wraps, in the side's colour.
 *  Named for assistive tech on the line on screen only. */
function TallySide({
  form,
  word,
  tally,
  tone,
  labelled,
}: {
  form: TallyForm;
  word: string;
  tally: PasurView["tallies"][number] | undefined;
  tone: string;
  labelled: boolean;
}): ReactNode {
  const clubs = tally?.clubs ?? 0;
  // a card the side holds: its rank and its pip, framed like a card (the clubs COUNT is unframed)
  const owns = (name: string, rank: number, suit: "clubs" | "diamonds"): ReactNode => (
    <span
      role={labelled ? "img" : undefined}
      aria-label={labelled ? fa.pasur.talliesOwner(name, word) : undefined}
      className="m-0 flex flex-none items-center rounded border border-solid border-current px-0.5 leading-none"
    >
      {faDigits(rank)}
      <Pip suit={suit} />
    </span>
  );
  return (
    <span className={`m-0 flex flex-none items-center gap-x-1 whitespace-nowrap ${tone}`}>
      {form === "words" ? <span className="m-0 text-on-surface-variant">{word}</span> : null}
      <span
        role={labelled ? "img" : undefined}
        aria-label={labelled ? fa.pasur.talliesClubs(word, clubs) : undefined}
        className="m-0 flex flex-none items-center"
      >
        <Pip suit="clubs" />
        {faDigits(clubs)}
      </span>
      {tally?.twoOfClubs ? owns(fa.pasur.breakdownTwoOfClubs, 2, "clubs") : null}
      {tally?.tenOfDiamonds ? owns(fa.pasur.breakdownTenOfDiamonds, 10, "diamonds") : null}
    </span>
  );
}

/** A form's parts: ما's tallies, then حریف's — each WHOLE. */
function TallyParts({
  form,
  view,
  labelled,
}: {
  form: TallyForm;
  view: PasurView;
  /** Named for assistive tech — the line on screen; never a form's measuring copy. */
  labelled: boolean;
}): ReactNode {
  const mine = view.yourSide;
  const words = pasurSideWords(view.seats);
  const sides = [
    { word: words.us, tally: view.tallies[mine], tone: "text-primary" },
    { word: words.them, tally: view.tallies[1 - mine], tone: "text-error" },
  ];
  return (
    <>
      {sides.map((s) => (
        <TallySide
          key={s.word}
          form={form}
          word={s.word}
          tally={s.tally}
          tone={s.tone}
          labelled={labelled}
        />
      ))}
    </>
  );
}

/**
 * THE RICHEST FORM THAT FITS — the index of the first of the box's form copies (`copies`, in the
 * forms' order, richest first) no bigger than the box along `axis` — or, given `room`, than that
 * element inside it —, or the number of copies when none is: the tallies under your name by WIDTH
 * (`[data-tallies-form]`), the deal's card by HEIGHT (`[data-breakdown-form]`, Task 8a), and a text
 * that must stay on TWO lines by HEIGHT against a two-line copy of its own box (`room`: the final
 * round's chip, a side seat's notice — Task 8b). Read off the LAID-OUT boxes with `getBoundingClientRect`
 * (`clientWidth` rounds, and a 0.4 px lie is a cut glyph), before the first paint (a layout effect),
 * and again when the box resizes (the status pill beside the tallies changes width with the turn; a
 * rotation; a text zoom), when a web font lands late, and when `key` changes (the tallies, the deal —
 * the box's size does not). The box's size never depends on the form shown (it fills its room, and
 * the copies are out of flow), so a pick cannot feed back into the next. Unlaid-out (happy-dom), every
 * size is 0: the richest form. بی‌دل's `useTopSeatRoom` reads its room the same way (its «no
 * ResizeObserver» note is its yield rule's).
 */
function useRichestFit(
  box: { current: HTMLElement | null },
  key: string | null,
  copies: string,
  axis: "width" | "height",
  room?: string,
): number {
  const [fit, setFit] = useState(0);
  useLayoutEffect(() => {
    const el = box.current;
    if (key === null || !el) return undefined;
    const read = (): void => {
      const roomEl = room === undefined ? el : el.querySelector<HTMLElement>(room);
      const size = roomEl ? roomEl.getBoundingClientRect()[axis] : 0;
      const forms = [...el.querySelectorAll<HTMLElement>(copies)];
      const i = forms.findIndex((c) => c.getBoundingClientRect()[axis] <= size);
      setFit(i < 0 ? forms.length : i);
    };
    read();
    const fonts: EventTarget | undefined = document.fonts;
    fonts?.addEventListener("loadingdone", read);
    const ro = typeof window.ResizeObserver === "function" ? new window.ResizeObserver(read) : null;
    ro?.observe(el);
    return () => {
      ro?.disconnect();
      fonts?.removeEventListener("loadingdone", read);
    };
  }, [box, key, copies, axis, room]);
  return fit;
}

/**
 * THE LIVE TALLIES, UNDER YOUR NAME — in the richest form that fits (above), every part WHOLE: nothing
 * in the line truncates, so no figure can ever be cut (Task 3's line was ONE truncating RTL box, and
 * its ellipsis sat on the figures). The box fills its column; each form's measuring copy rides in it,
 * invisible, out of flow and hidden from assistive tech. Every form draws its clubs counts as text, so
 * the line holds its height in every phase and your strip is ONE height (بی‌دل's floor, review 6b F1).
 */
function TalliesLine({
  view,
  style,
}: {
  view: PasurView;
  /** Its place on your strip's grid, where the strip takes two rows (`useStripRows`). */
  style?: CSSProperties | undefined;
}): ReactNode {
  const boxRef = useRef<HTMLSpanElement>(null);
  const key = view.tallies
    .map((t) => `${t.clubs}:${t.twoOfClubs ? 1 : 0}:${t.tenOfDiamonds ? 1 : 0}`)
    .join("|");
  const fit = useRichestFit(boxRef, `${view.yourSide}/${key}`, "[data-tallies-form]", "width");
  // past the last form, the last: never one that drops a side's clubs or an owner (card-a7 B8)
  const form: TallyForm = TALLY_FORMS[fit] ?? "numbers";
  return (
    <span
      ref={boxRef}
      data-tallies={form}
      data-tallies-held={cardsHeld(view.tallies)}
      className={`${TALLY_ROW} relative w-full min-w-0 font-caption text-caption text-on-surface-variant`}
      style={style}
    >
      <TallyParts form={form} view={view} labelled />
      {TALLY_FORMS.map((f) => (
        <span
          key={f}
          aria-hidden="true"
          data-tallies-form={f}
          className={`${TALLY_ROW} invisible pointer-events-none absolute start-0 top-0`}
        >
          <TallyParts form={f} view={view} labelled={false} />
        </span>
      ))}
    </span>
  );
}

/** Your strip's grid where it takes two rows (`useStripRows`): your ring's track (it gives way first,
 *  as its group does in one row), your name's, the status group's. */
const STRIP_TWO_ROW_COLUMNS = "minmax(0, auto) minmax(0, 1fr) auto";

/** The widest the tallies can draw (card-a7 B8) — what `useStripRows` sizes your strip for: both
 *  cards held (on one side or split, the same parts and gaps) and the clubs at their widest a deal
 *  reaches — the deck's 13 between the sides, so only ONE side can have two figures: 13 + 0, 12 + 1,
 *  11 + 2, 10 + 3 (each measured, the widest taken: a font's figures need not be one width). */
const WORST_TALLIES: readonly PasurView["tallies"][] = [
  [13, 0],
  [12, 1],
  [11, 2],
  [10, 3],
].map(([a, b]) => [
  { cards: 0, clubs: a!, aces: 0, jacks: 0, twoOfClubs: true, tenOfDiamonds: true, surs: 0 },
  { cards: 0, clubs: b!, aces: 0, jacks: 0, twoOfClubs: false, tenOfDiamonds: false, surs: 0 },
]);

/**
 * YOUR STRIP'S WORST CASE, MEASURED (card-a7 B8) — out of flow, invisible, hidden from assistive tech:
 * the tallies' `numbers` line at its widest (`WORST_TALLIES`, stacked in one cell), and the status
 * group at its widest —
 * every word the pill can say (in its muted dress, the wider padding) stacked in one cell, then the
 * timer's slot (counted at a solo table too, so the choice is the same at every table). The pill's
 * words are generated content (`data-copy-text`), so no probe or test that finds the pill by its
 * words finds a copy.
 */
function StripWorst({
  view,
  copiesRef,
}: {
  view: PasurView;
  copiesRef: RefObject<HTMLDivElement>;
}): ReactNode {
  const words = [fa.pasur.yourTurn, fa.pasur.partnerTurn, fa.pasur.opponentTurn, fa.pasur.gameOver];
  return (
    <div
      ref={copiesRef}
      aria-hidden="true"
      className="invisible pointer-events-none absolute start-0 top-0 flex w-max flex-col items-start"
    >
      <span data-strip-worst="line" className="grid font-caption text-caption">
        {WORST_TALLIES.map((tallies) => (
          <span key={tallies[0]!.clubs} className={TALLY_ROW} style={{ gridArea: "1 / 1" }}>
            <TallyParts form="numbers" view={{ ...view, tallies }} labelled={false} />
          </span>
        ))}
      </span>
      <span data-strip-worst="status" className="flex flex-none items-center gap-2">
        <span className="grid">
          {words.map((w) => (
            <span key={w} className="flex" style={{ gridArea: "1 / 1" }}>
              <StatusPill gold={false}>
                <span data-copy-text={w} className={COPY_TEXT} />
              </StatusPill>
            </span>
          ))}
        </span>
        <span className="block h-8 w-8 flex-none" />
      </span>
    </div>
  );
}

/** Your panel's top padding over one row (`pt-2`) — what a second row's height comes out of. */
const PANEL_PAD_TOP = 8;
/** One row's gap between your name and the tallies' line (its column's `gap-0.5`) — two rows have
 *  none (`useStripRows`). */
const ONE_ROW_LINE_GAP = 2;

/**
 * ONE ROW OR TWO — your strip's shape, decided per SCREEN and never per state (card-a7 B8), so it is
 * one height in every phase (`frame:strip-height`) and the timer's slot stays put: TWO rows wherever
 * the widest tallies line (`StripWorst`) cannot sit beside your ring and the widest status group —
 * your name, the pill and the slot on the first, the tallies' line the whole width under them; ONE
 * (the strip as it was) wherever it can. Read off the laid-out boxes before the first paint, again
 * when the row or the copies resize (a rotation, a text zoom), when a web font lands late, and once
 * the shape is drawn. The row's width, the ring's and the copies' never depend on the shape shown,
 * so a pick cannot feed back.
 * THE FELT KEEPS ITS ROOM (`pad`): over two rows, your panel's top padding is what one row would
 * leave — 8 px less the height the second row adds over one row's (your name over the line, the
 * ring, the status group, the row's floor: the parts' own heights), never below 0 — so the panel is
 * the height it was and the felt above it is not moved by a pixel. The two rows have NO gap between
 * them, so the first row (the status pill) sits no higher than one row put your strip's top
 * wherever the second row adds no more than the 2 px one row's column gap took (×1.5: 62 + 8 = 62
 * + 8 — the pill at one row's strip top, not 2 px above it, where 256×393@1.5's top-seat chip
 * already hangs below the felt; ×1: 48 + 2 = 42 + 8). (Before: ×1: 50 + 0 = 42 + 8; ×1.5: 64 +
 * 6 = 62 + 8). Null over one row (`pt-2`), and until two rows are drawn.
 * Unlaid-out (happy-dom), every box is 0: one row.
 */
function useStripRows(
  row: RefObject<HTMLDivElement>,
  ring: RefObject<HTMLDivElement>,
  copies: RefObject<HTMLDivElement>,
): { two: boolean; pad: number | null } {
  const [shape, setShape] = useState<{ two: boolean; pad: number | null }>({
    two: false,
    pad: null,
  });
  const drawnTwo = shape.two;
  useLayoutEffect(() => {
    const rowEl = row.current;
    const ringEl = ring.current;
    const copiesEl = copies.current;
    if (!rowEl || !ringEl || !copiesEl) return undefined;
    const read = (): void => {
      const box = rowEl.getBoundingClientRect();
      if (!(box.width > 0)) return;
      const cs = getComputedStyle(rowEl);
      const gap = parseFloat(cs.columnGap) || 0;
      const width = (part: string): number =>
        copiesEl.querySelector<HTMLElement>(`[data-strip-worst="${part}"]`)?.getBoundingClientRect()
          .width ?? 0;
      const need =
        ringEl.getBoundingClientRect().width + gap + width("line") + gap + width("status");
      const two = need > box.width;
      let pad: number | null = null;
      if (two && drawnTwo) {
        const height = (el: Element | null): number => el?.getBoundingClientRect().height ?? 0;
        const one = Math.max(
          parseFloat(cs.minHeight) || 0,
          height(ringEl),
          height(rowEl.querySelector('span[dir="auto"]')) +
            ONE_ROW_LINE_GAP +
            height(rowEl.querySelector("[data-tallies]")),
          height(rowEl.querySelector("[data-strip-status]")),
        );
        pad = Math.min(PANEL_PAD_TOP, Math.max(0, PANEL_PAD_TOP - (box.height - one)));
      }
      setShape((s) => (s.two === two && s.pad === pad ? s : { two, pad }));
    };
    read();
    const fonts: EventTarget | undefined = document.fonts;
    fonts?.addEventListener("loadingdone", read);
    const ro = typeof window.ResizeObserver === "function" ? new window.ResizeObserver(read) : null;
    ro?.observe(rowEl);
    ro?.observe(copiesEl);
    return () => {
      ro?.disconnect();
      fonts?.removeEventListener("loadingdone", read);
    };
  }, [row, ring, copies, drawnTwo]);
  return shape;
}

/**
 * THE ROOM A MOMENT'S BANNER HAS — MEASURED, never a breakpoint (Task 8a fix round 1, F1). The banner
 * sits centred in the felt's notices column, and under a text zoom the SIDE seats' boxes (the name, a
 * wrapped notice, the سور chip under it) grow down to its row: at 320×568 ×1.5 the chips end at 259,
 * the row starts at 254, and the 201 px banner lay 7.8 px over each. Where a side seat's box reaches
 * the row (within POOL_CLEAR) the banner is bounded to the room between the side seats' columns,
 * POOL_CLEAR clear of each — the pool's own rule (`POOL_SIDE`): 180 px there. Where none reaches, null
 * — the column, as before: always bounding would cut names where nothing overlaps (384×823 ×1.3: a
 * 238 px cancel in a 220 px bound, the side seats ending 96 px above it). The top seat is across, over
 * the pool: bounding the sides never clears it, so it bounds nothing. Read as `usePoolRoom` reads its
 * room: off the laid-out boxes before the first paint, again when the row, the felt or a side seat
 * resizes (a notice, a chip, the zoom, a rotation) or a late web font lands. Unlaid-out (happy-dom),
 * every box is 0: null.
 */
function useBannerRoom(
  row: RefObject<HTMLDivElement>,
  felt: RefObject<HTMLDivElement>,
  sides: readonly number[],
): number | null {
  const [room, setRoom] = useState<number | null>(null);
  const key = sides.join(",");
  useLayoutEffect(() => {
    const rowEl = row.current;
    const feltEl = felt.current;
    if (!rowEl || !feltEl) return undefined;
    const seatEls = (key === "" ? [] : key.split(","))
      .map((s) => feltEl.parentElement?.querySelector<HTMLElement>(`[data-seat="${s}"]`) ?? null)
      .filter((el): el is HTMLElement => el !== null);
    const read = (): void => {
      const r = rowEl.getBoundingClientRect();
      if (!(r.width > 0 && r.height > 0)) return;
      const mid = (r.left + r.right) / 2;
      let half: number | null = null;
      for (const el of seatEls) {
        const s = el.getBoundingClientRect();
        if (!(s.width > 0 && s.height > 0)) continue;
        if (s.bottom <= r.top - POOL_CLEAR || s.top >= r.bottom + POOL_CLEAR) continue;
        const h = s.right <= mid ? mid - s.right : s.left - mid;
        half = half === null ? h : Math.min(half, h);
      }
      setRoom(half === null ? null : Math.max(0, 2 * (half - POOL_CLEAR)));
    };
    read();
    const fonts: EventTarget | undefined = document.fonts;
    fonts?.addEventListener("loadingdone", read);
    const ro = typeof window.ResizeObserver === "function" ? new window.ResizeObserver(read) : null;
    for (const el of [rowEl, feltEl, ...seatEls]) ro?.observe(el);
    return () => {
      ro?.disconnect();
      fonts?.removeEventListener("loadingdone", read);
    };
  }, [row, felt, key]);
  return room;
}

/**
 * A MOMENT'S BANNER — a سور, its cancel, the scoop's label — in the felt's notices column: ONE
 * `BANNER_PILL` (`dir="auto"`), its line the richest of `lines` (`bannerLines`, richest first) that
 * fits the room `useBannerRoom` measures, picked by WIDTH (`useRichestFit`, as the tallies' line is);
 * where none fits (a best-effort size), the tightest, cut at the room as the pill always cut. Each
 * form's measuring copy rides in the box — invisible, out of flow, unheard and unnamed, never popping —
 * and the pill is the box's one in-flow child: the row is the pill's height, as before, so the pool's
 * room above the column never moves. `min-w-0`: a flex item's automatic minimum (the pill's unbroken
 * line) would otherwise beat the room's `max-width`.
 */
function MomentBanner({
  id,
  lines,
  felt,
  sides,
}: {
  id: "pasur-sur" | "pasur-sur-cancel" | "pasur-scoop";
  lines: readonly string[];
  felt: RefObject<HTMLDivElement>;
  /** The side seats (left and right of you): the seats whose columns can reach the banner's row. */
  sides: readonly number[];
}): ReactNode {
  const rowRef = useRef<HTMLDivElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const room = useBannerRoom(rowRef, felt, sides);
  const fit = useRichestFit(
    boxRef,
    `${room ?? "col"}|${lines.join("\n")}`,
    "[data-banner-form]",
    "width",
  );
  const line = lines[Math.min(fit, lines.length - 1)] ?? "";
  return (
    <div ref={rowRef} data-banner-row="" className="flex w-full justify-center">
      <div
        ref={boxRef}
        data-banner-room=""
        className="relative flex w-full min-w-0 justify-center"
        style={room === null ? undefined : { maxWidth: room }}
      >
        <span
          data-testid={id}
          dir="auto"
          className={BANNER_PILL}
          style={{ animation: "pop-in-glow .35s ease", fontWeight: 800 }}
        >
          {line}
        </span>
        {lines.map((l, i) => (
          <span
            key={i}
            aria-hidden="true"
            data-banner-form={i}
            dir="auto"
            className={`${BANNER_PILL} invisible pointer-events-none absolute start-0 top-0`}
            // inline: a `max-w-none` class could lose to the pill's `max-w-full` by stylesheet order
            style={{ fontWeight: 800, maxWidth: "none", width: "max-content" }}
          >
            {l}
          </span>
        ))}
      </div>
    </div>
  );
}

/**
 * THE DEAL'S CARD'S FORMS, richest first (Task 8a, residual 1) — where the felt is shorter than the
 * whole card it gives way in this order instead of scrolling (256×393@1.5 under a ×1.3 text zoom: the
 * card's room is 85 px and the whole card 205 — its totals sat below the fold for its 3 s; 320×568
 * under ×1.5: 228 px for 262):
 *   full     the scoop's line, the head, every line — clubs, 2♣, 10♦, aces, Jacks, سور, this deal,
 *            the totals;
 *   lines    every line without the scoop's (a name: the felt's own label said it as the remainder
 *            slid) — the numbers before the name;
 *   compact  the scoop's line, the head, this deal and the totals — the numbers a player reads first;
 *   tight    the head, this deal and the totals.
 * A deal with no remainder has no scoop's line, so two: full and compact. Picked by HEIGHT against
 * the scrim's room (`useRichestFit`, as the tallies' line is picked by width — a viewport breakpoint
 * cannot see the text zoom); where not even the tightest fits (a best-effort size), the tightest,
 * scrolling from its top as the whole card did.
 */
const BREAKDOWN_FORMS = ["full", "lines", "compact", "tight"] as const;
/** The card's box — the card's, and its scoop row's measuring room's (`BreakdownLayer`). */
const CARD_CHROME =
  "rounded-2xl border border-solid border-secondary/60 bg-surface-container/95 px-3 py-2 font-caption text-caption text-on-surface shadow-lg";
/** The scoop's row's line — on the card, and its measuring copies'. */
const SCOOP_ROW = "whitespace-nowrap font-bold text-secondary";
type BreakdownForm = (typeof BREAKDOWN_FORMS)[number];
/** The forms a card can take: all four with a scoop's line, else the two that differ. */
const formsFor = (scoop: boolean): readonly BreakdownForm[] =>
  scoop ? BREAKDOWN_FORMS : ["full", "compact"];
/** The lines a SHORT form keeps — the deal's points and the totals. */
const SHORT_LINES: readonly string[] = ["deal", "total"];

/**
 * THE DEAL'S BREAKDOWN CARD (Task 7, W15) — after the deal's end (its last play, then the scoop) and
 * before the next deal is dealt; at the match's end, before the end card (note 2). It sits over the
 * FELT by design: the deal is over, nothing on the felt can be played or read for its
 * PASUR_BREAKDOWN_MS, and at 320 px under a text zoom no legible card fits beside the seats — so a
 * felt-shaped scrim covers the seats and the corner chips and the card is centred on it; never the
 * header band (its totals bounce as the card shows) nor your strip. Every number is the deal's result's
 * (`sideBreakdown`, the engine's values); the scoop's line is its first row (`pasur-scoop`), so the
 * felt's own label goes once the remainder has landed. A transform-only pop (no opacity ramp: it is
 * painted from its first frame).
 */
function BreakdownCard({
  deal,
  yourSide,
  seats,
  target,
  scoopLine,
  form,
  copy = false,
}: {
  deal: PasurDealResult;
  yourSide: number;
  /** The table's size: at two seats the viewer's side is «شما» (`pasurSideWords`). */
  seats: number;
  /** The match's target («تا ۶۲»), off the view. */
  target: number;
  /** The scoop's line in the form its row holds (`BreakdownLayer`) — null when there was no scoop. */
  scoopLine: string | null;
  /** Which rows it draws (`BREAKDOWN_FORMS`). */
  form: BreakdownForm;
  /** A form's MEASURING copy (`BreakdownLayer`): no pop, no names for assistive tech or the probes. */
  copy?: boolean;
}): ReactNode {
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!copy && el && typeof el.animate === "function")
      el.animate([{ transform: "scale(.92)" }, { transform: "none" }], {
        duration: 220,
        easing: "cubic-bezier(.2,.8,.2,1)",
      });
  }, [copy]);
  const sides = [
    { side: "us", tone: "text-primary", b: sideBreakdown(deal, yourSide) },
    { side: "them", tone: "text-error", b: sideBreakdown(deal, 1 - yourSide) },
  ] as const;
  const points = (n: number): string => (n > 0 ? faDigits(n) : fa.pasur.breakdownNone);
  const lines: {
    id: string;
    label: string;
    value: (b: SideBreakdown) => ReactNode;
    /** What the cell says to assistive tech where its drawing is not words (the clubs' arrow). */
    said?: (b: SideBreakdown) => string;
    strong?: boolean;
  }[] = [
    {
      id: "clubs",
      label: fa.pasur.breakdownClubs,
      said: (b) => fa.pasur.clubsOfLabel(b.clubs.count, CLUBS_IN_A_DECK, b.clubs.points),
      value: (b) => (
        <>
          {fa.pasur.clubsOf(b.clubs.count, CLUBS_IN_A_DECK)}
          {b.clubs.points > 0 ? (
            <>
              <span aria-hidden="true" className="mx-0.5 inline-block align-middle">
                <GameIcon type="back" size={10} />
              </span>
              {faDigits(b.clubs.points)}
            </>
          ) : null}
        </>
      ),
    },
    { id: "twoOfClubs", label: fa.pasur.breakdownTwoOfClubs, value: (b) => points(b.twoOfClubs) },
    {
      id: "tenOfDiamonds",
      label: fa.pasur.breakdownTenOfDiamonds,
      value: (b) => points(b.tenOfDiamonds),
    },
    { id: "aces", label: fa.pasur.breakdownAces, value: (b) => points(b.aces.points) },
    { id: "jacks", label: fa.pasur.breakdownJacks, value: (b) => points(b.jacks.points) },
    {
      id: "surs",
      label: fa.pasur.breakdownSurs,
      value: (b) =>
        b.surs.count > 0 ? fa.pasur.surTimes(b.surs.count, POINTS.SUR) : fa.pasur.breakdownNone,
    },
    {
      id: "deal",
      label: fa.pasur.breakdownDeal,
      value: (b) => `+${faDigits(b.points)}`,
      strong: true,
    },
    {
      id: "total",
      label: fa.pasur.target(target),
      value: (b) => faDigits(b.total),
      strong: true,
    },
  ];
  // the rows this form keeps: every line, or the deal's points and the totals; the scoop's line
  const rows =
    form === "full" || form === "lines" ? lines : lines.filter((l) => SHORT_LINES.includes(l.id));
  const scoop = scoopLine !== null && (form === "full" || form === "compact") ? scoopLine : null;
  return (
    <div
      ref={ref}
      data-testid={copy ? undefined : "pasur-breakdown"}
      data-form={copy ? undefined : form}
      role={copy ? undefined : "status"}
      aria-label={copy ? undefined : fa.pasur.breakdownTitle}
      className={`m-auto max-w-full ${CARD_CHROME}`}
    >
      {scoop !== null ? (
        <p
          data-testid={copy ? undefined : "pasur-scoop"}
          className={`m-0 mb-1 truncate text-center ${SCOOP_ROW}`}
        >
          {scoop}
        </p>
      ) : null}
      <div
        className="grid items-baseline gap-x-3 whitespace-nowrap"
        style={{ gridTemplateColumns: "auto auto auto" }}
      >
        <span aria-hidden="true" />
        {sides.map((s) => (
          <span key={s.side} className={`m-0 text-center font-bold ${s.tone}`}>
            {s.side === "us" ? pasurSideWords(seats).us : pasurSideWords(seats).them}
          </span>
        ))}
        {rows.map((line) => (
          <Fragment key={line.id}>
            <span className={`m-0 ${line.strong ? "font-bold" : "text-on-surface-variant"}`}>
              {line.label}
            </span>
            {sides.map((s) => (
              <span
                key={s.side}
                dir="ltr"
                role={line.said && !copy ? "img" : undefined}
                aria-label={copy ? undefined : line.said?.(s.b)}
                data-line={copy ? undefined : line.id}
                data-breakdown-side={copy ? undefined : s.side}
                className={`m-0 text-center ${line.strong ? `font-bold ${s.tone}` : ""}`}
              >
                {line.value(s.b)}
              </span>
            ))}
          </Fragment>
        ))}
      </div>
    </div>
  );
}

/**
 * THE DEAL'S CARD ON ITS SCRIM (Task 7, D2: over the felt's own rect, above the seats, for its whole
 * span) — in the richest form its room holds (Task 8a: `BREAKDOWN_FORMS`). The card is centred by its
 * AUTO MARGINS, never by the box's alignment: where the felt is shorter than even the tightest form it
 * starts at the top and scrolls, while a centred flex item would overflow upward too, out of the
 * scroll's reach. Each form's measuring copy rides in a box that IS the card's room (the scrim's
 * content box), invisible, unheard, clipped so no copy can make the scrim scroll.
 */
function BreakdownLayer({
  deal,
  yourSide,
  seats,
  target,
  scoopLines,
}: {
  deal: PasurDealResult;
  yourSide: number;
  seats: number;
  target: number;
  /** The scoop's line in every form, richest first (`bannerLines`) — null when there was no scoop. */
  scoopLines: readonly string[] | null;
}): ReactNode {
  const roomRef = useRef<HTMLDivElement>(null);
  const scoopRef = useRef<HTMLDivElement>(null);
  const forms = formsFor(scoopLines !== null);
  // the scoop's line names its player by the longest start its row holds, by WIDTH (final review B,
  // N2: a CSS cut falls at the line's END, the physical left, where a Latin name STARTS) — as a
  // moment's banner does; where none fits (a best-effort size), the tightest, cut as before
  const scoopFit = useRichestFit(
    scoopRef,
    scoopLines === null ? null : `${deal.dealNumber}|${scoopLines.join("\n")}`,
    "[data-scoop-form]",
    "width",
  );
  const scoopLine =
    scoopLines === null ? null : (scoopLines[Math.min(scoopFit, scoopLines.length - 1)] ?? null);
  const fit = useRichestFit(
    roomRef,
    `${deal.dealNumber}/${forms.length}`,
    "[data-breakdown-form]",
    "height",
  );
  // none fits (a best-effort size): the tightest, scrolling
  const form = forms[Math.min(fit, forms.length - 1)]!;
  const props = { deal, yourSide, seats, target, scoopLine };
  return (
    <div
      data-breakdown-layer=""
      className="absolute inset-3 z-[30] flex overflow-y-auto p-3"
      style={{ borderRadius: 40, background: "rgb(var(--c-felt-deep) / .86)" }}
    >
      <BreakdownCard {...props} form={form} />
      <div
        ref={roomRef}
        data-breakdown-room=""
        aria-hidden="true"
        className="pointer-events-none invisible absolute inset-3 overflow-hidden"
      >
        {forms.map((f) => (
          <div key={f} data-breakdown-form={f} className="absolute start-0 top-0 max-w-full">
            <BreakdownCard {...props} form={f} copy />
          </div>
        ))}
        {/* the scoop's row's room: the card's chrome at the room's whole width — its content box is
            the widest the row can be — and each of the line's forms, unbroken, measured in it */}
        {scoopLines !== null ? (
          <div className={`absolute start-0 top-0 w-full ${CARD_CHROME}`}>
            <div ref={scoopRef} data-scoop-room="" className="relative">
              {scoopLines.map((l, i) => (
                <p
                  key={i}
                  data-scoop-form={i}
                  className={`absolute start-0 top-0 m-0 ${SCOOP_ROW}`}
                  style={{ maxWidth: "none", width: "max-content" }}
                >
                  {l}
                </p>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

/** A seat's card on its way out of the stock — Card's `small` box, centred on the place it is put. */
const DEAL_BACK_STYLE: CSSProperties = {
  left: 0,
  top: 0,
  width: 36,
  height: 50,
  marginLeft: -18,
  marginTop: -25,
  opacity: 0,
};

/** The stock's count `t` ms into a deal: one down as each card leaves, one up as a Jack is back in. */
const stockAt = (plan: DealPlan, t: number): number =>
  plan.stock.reduce((n, e) => (e.at <= t ? n + e.delta : n), plan.before);

/** THE STOCK'S COUNT WHILE A DEAL LEAVES IT (Task 6) — on its own clock (one timer per change), so the
 *  table does not re-render per card. Keyed per deal by the caller. */
function DealStockCount({ plan }: { plan: DealPlan }): ReactNode {
  const [count, setCount] = useState(() => stockAt(plan, 0));
  useEffect(() => {
    const times = [...new Set(plan.stock.map((e) => e.at))].filter((t) => t > 0);
    const timers = times.map((t) => setTimeout(() => setCount(stockAt(plan, t)), t));
    return () => timers.forEach(clearTimeout);
  }, [plan]);
  return <>{faDigits(count)}</>;
}

export function PasurTable({
  view,
  legal,
  onMove,
  onLeave,
  self,
  players,
  seatNotices,
  timer,
  onInviteSeat,
  chat = false,
  liftToPlay = getLiftToPlay(),
  onHolding,
}: {
  view: PasurView;
  legal: readonly PasurMove[];
  onMove: (move: PasurMove) => void;
  onLeave: () => void;
  /** The signed-in user — renders YOUR seat with your real photo + name. */
  self?: SeatIdentity | null;
  /** The lobby roster: real occupants get their own name, face and verified badge; a bot (or a
   *  human the server could not resolve) keeps the positional stand-in. */
  players?: readonly SeatPlayer[];
  /** Online: what has just happened to each seat, said on that seat (`seat-notice.tsx`). */
  seatNotices?: readonly SeatNotice[] | undefined;
  /** The live turn countdown (online: the countdown, or null while nobody's turn is timed; solo:
   *  never passed) — drawn in your strip's one fixed slot (`TurnTimerSlot`, حکم's and بی‌دل's). */
  timer?: TableTurnTimer | null;
  /** FEATURE 3 — set (online, host, mid-game) so tapping a BOT chair invites a friend into it. */
  onInviteSeat?: ((seat: number) => void) | undefined;
  /** Online: the shell's table-chat toggle sits beside the exit on EVERY online table, so its
   *  footprint is kept free from the first frame (see CHAT_TOGGLE_PX); solo has none. */
  chat?: boolean;
  /** «تأیید قبل از بازی کارت» (`game/lift-to-play.ts`, حکم's and بی‌دل's): a card tap LIFTS the card
   *  and previews its capture, a tap on the felt (one legal move), on the card or on «جمع کن» plays
   *  it. The table reads the Settings pref itself — it cannot change under a mounted table. A card
   *  with several capture sets lifts whatever the pref (the picker, `pasur-capture.ts`). */
  liftToPlay?: boolean;
  /**
   * Told whether a moment is on screen or waiting (`useCaptureHold`'s `holding`) whenever that changes —
   * and «not holding» when the table goes away. The containers hold the END CARD until it is false
   * (Task 8a, residual 4): the match's last deal end and its card are always shown whole, whatever the
   * terminal frame brought ahead of them.
   */
  onHolding?: ((holding: boolean) => void) | undefined;
}): ReactNode {
  const you = view.you;
  const seats = view.seats;
  // A zoomed screen (the 384×823 phone, Telegram Desktop): smaller seats, your own avatar too.
  const compact = useCompactTable();
  // Names and faces come from ONE place, so a seat reads the same on the felt and on the end card.
  const nameOf = (seat: number): string =>
    resolveSeatName(seat, you, self, players, { you: fa.pasur.you, fallback: pasurSeatFallback });
  const avatarOf = (seat: number): SeatAvatar =>
    resolveSeatAvatar(seat, you, self, players, presetAvatarFor);
  // FEATURE 3 — the host's «+» on a BOT chair only (your own seat is never a bot).
  const inviteFor = (seat: number): (() => void) | undefined =>
    onInviteSeat && players?.find((p) => p.seat === seat)?.isBot
      ? () => onInviteSeat(seat)
      : undefined;
  // One «play» cue + one send per server frame (a card re-tapped while its move is in flight is
  // ignored). See useFrameKey / Card.commitKey.
  const frameKey = useFrameKey(view);

  // THE MOMENTS (`use-capture-hold.ts`): a capture, a سور, its cancel and a deal's scoop each stay on
  // screen for their whole hold + collect, latched on the render where they first appear. While one
  // is on, the table draws `shown` — the table as the moment's play found it (its pool, its numbers,
  // its stock, its deal; your hand less the card you played) — and the hand is disabled. Only the
  // timer, the seat notices and the server's `legal` stay live.
  const hold = useCaptureHold(view, (seat) => pasurSeatEdge(seat, you, seats));
  const shown = hold.shown;
  // …and the containers hear when the last of them has gone: the end card waits for it (a boolean,
  // never the frame: the socket-frame rule)
  const holding = hold.holding;
  useEffect(() => {
    onHolding?.(holding);
    return () => onHolding?.(false);
  }, [holding, onHolding]);
  // THE DEAL ON SCREEN (Task 6, W14): a round of the deal coming out of the stock — its plan, card by
  // card (`dealPlan`), stable while it holds; `use-pasur-deal.ts` flies it
  const dealing = hold.moment?.kind === "deal" ? hold.moment : null;
  // THE DEAL'S CARD ON SCREEN (Task 7): nobody's move while it shows — no turn named, no seat lit
  const breakdown = hold.moment?.kind === "breakdown" ? hold.moment : null;
  const plan = useMemo(() => (dealing === null ? null : dealPlan(dealing)), [dealing]);

  // Table sounds, read off what the felt SHOWS (as 1fa3c258 did for حکم and بی‌دل): the deal and every
  // refill round shuffle as their deal starts — at a mount only the match's first frame, whose deal is
  // on screen: a resume, on a later deal's first frame too, plays no phantom shuffle (Task 6); another
  // seat's card clicks as it appears — a card that landed during a hold, at the release. A capture's
  // own cue sounds as its set starts to slide (the hold's).
  const plays = shown.playLog ?? [];
  const lastPlay = plays[plays.length - 1];
  useDealSfx(shown.hand.length, dealing !== null);
  useCardLandSfx(
    lastPlay ? { seat: lastPlay.seat, key: `${plays.length}:${lastPlay.card}` } : null,
    you,
  );

  const playing = shown.phase === "playing";
  const isYourTurn = playing && shown.turn === you;
  // YOUR MOVE — the gold pill, your ring's glow, the prompt and «جمع کن» — only while your hand can
  // act. While a moment holds, the strip and the seats' glow follow the frame the felt draws (the
  // play's own seat, `momentView`) — and YOUR OWN move's moment names no turn at all: never «نوبت
  // شماست» over a disabled hand, muted or not (fix round 1, F2b; the controller's veto) — and nor does
  // a DEAL, nobody's move yet (Task 6). The pill keeps its place, invisible, in the box of the pill it
  // stands for — the GOLD one where that is your move (note 9b: the muted box is 4 px wider, and the
  // strip moved), so nothing in the strip moves.
  const yourMove = isYourTurn && !hold.holding;
  const pillHidden = hold.holding && (isYourTurn || dealing !== null || breakdown !== null);
  // THE STRIP'S WORDS — true for whoever reads them: your move; else whose it is, by SIDE (never
  // «منتظر بازیکنان…» mid-game, which reads as a table still filling); or that the match is over.
  const statusText = !playing
    ? fa.pasur.gameOver
    : isYourTurn
      ? fa.pasur.yourTurn
      : sideOf(shown.turn) === view.yourSide
        ? fa.pasur.partnerTurn
        : fa.pasur.opponentTurn;

  // The match, per SIDE: «ما» is the viewer's side, from either side — as the felt shows it (a deal's
  // points join the totals only once its last play and its scoop have been seen).
  const usTotal = shown.totalScores[view.yourSide] ?? 0;
  const themTotal = shown.totalScores[1 - view.yourSide] ?? 0;

  // WHO PLAYED each card now in the pool — the public `playLog` is the only record of it. A trailed
  // card flies in FROM ITS PLAYER'S SEAT (the diamond's edge for that seat); a card DEALT face-up has
  // no log entry and keeps the neutral bottom entrance (Task 6 deals it from the stock).
  const playedBy = useMemo(() => {
    const by = new Map<string, number>();
    for (const p of shown.playLog ?? []) by.set(p.card, p.seat);
    return by;
  }, [shown.playLog]);

  // THE CAPTURE PICKER (`pasur-capture.ts`) ON THE SHARED LIFT (`lift-to-play.ts`). Legality comes
  // from the server's list ONLY: each playable hand card → its capture sets ([] = it trails).
  const options = useMemo(() => optionsByCard(legal), [legal]);
  // What a tap LIFTS: every playable card under the pref; a card with several sets whatever the
  // pref (the choice is the player's — R4's accessibility clause). The rest play on one tap.
  const liftable = useMemo(() => liftableCards(options, liftToPlay), [options, liftToPlay]);
  // The set the player chose, for which lifted card, in which frame — DERIVED below like the lift
  // itself: another card's, another frame's or a vanished set's pick counts for nothing.
  const [pick, setPick] = useState<Pick | null>(null);
  const pickKeyFor = (card: string): string | null =>
    pick && pick.card === card && pick.frame === frameKey ? pick.key : null;
  // The hook owns the lift, the once-per-frame latch and the one «play» cue; `onPlay` resolves the
  // card to its move (its trail, its lone set, or the chosen one) and `retap` says what a second tap
  // on the lifted card does (the pref ON: it goes back down, every card, as on حکم — the setting's
  // rule; OFF: a settled capture plays, an open choice only nudges). `enabled` is always on: the
  // pref decides what lifts (`liftable`), not whether the picker exists. While a capture holds, the
  // hook is inert: nothing lifts, nothing sends (`holding`).
  const liftPlay = useLiftToPlay({
    enabled: true,
    holding: hold.holding,
    frameKey,
    playable: liftable,
    retap: (card) => retapOf(options, card, pickKeyFor(card), liftToPlay),
    onPlay: (card) => {
      const move = moveFor(options, card, pickKeyFor(card));
      if (!move) return false; // nothing to send (an open choice): no latch, no cue
      onMove(move);
      return true;
    },
  });
  const picker = derivePicker(liftPlay.lifted, pick, options, frameKey);
  // A tap on a pool card chooses the set holding it (and cycles through the sets holding it); a
  // send in flight freezes the choice the server is about to answer. A lifted ONE-move card — a
  // trail, or a lone set whose ringed target is the natural confirm — PLAYS on any pool card,
  // exactly as on the bare felt (the controller's ruling over R4's «a tap on the table»: at 320 a
  // full pool leaves no bare felt); a card with several sets only ever selects or cycles here, and
  // «جمع کن» confirms it (R4 (b), (c)).
  const tapPool = (cardId: string): void => {
    if (liftPlay.inFlight() || picker.lifted === null) return;
    const key = pickOnPool(picker, cardId);
    if (key !== null && key !== picker.selectedKey)
      setPick({ card: picker.lifted, key, frame: frameKey });
    else if (feltPlays(options, picker.lifted, liftToPlay)) liftPlay.play();
  };
  // THE CHOICE BETWEEN SETS, MADE OBVIOUS (B3 item 2, operator 2026-10-20): while the lifted card has
  // SEVERAL sets, each set is its own chip in the felt's bottom row (`pasur-prompt`'s place) — a tap
  // chooses THAT set, as a tap on one of its cards does — and a pool card in no set is drawn not
  // choosable (`Card`'s disabled look). The chosen chip and its cards (ring, count) light together.
  const choosing = picker.sets.length > 1;
  const chooseSet = (key: string): void => {
    if (liftPlay.inFlight() || picker.lifted === null || key === picker.selectedKey) return;
    setPick({ card: picker.lifted, key, frame: frameKey });
  };
  // …the question beside the chips only where the row holds both (its measuring copy, below)
  const feltRowRef = useRef<HTMLDivElement>(null);
  const setsFit = useRichestFit(
    feltRowRef,
    yourMove && choosing ? `${picker.lifted}|${picker.sets.map(optionKey).join("/")}` : null,
    "[data-sets-form]",
    "width",
  );
  // card-91's (b): a second tap on a lifted card whose choice is still open does nothing beyond a
  // light nudge of the card itself (WAAPI, guarded: happy-dom has no `animate`).
  const handRef = useRef<HTMLDivElement>(null);
  const nudgeLifted = (): void => {
    const el = handRef.current?.querySelector<HTMLElement>('[data-lifted="1"]');
    if (el && typeof el.animate === "function")
      el.animate(
        [
          { transform: "translateY(0)" },
          { transform: "translateY(-4px)" },
          { transform: "translateY(0)" },
        ],
        { duration: 180, easing: "ease-out" },
      );
  };

  const opponents = Array.from({ length: seats }, (_u, seat) => seat).filter((s) => s !== you);
  // the sides' words — «شما» for the viewer at a table for two (`pasurSideWords`)
  const sideWords = pasurSideWords(seats);
  const ownName = nameOf(you);
  const ownAvatar = avatarOf(you);
  // THE DEALER'S MARKER (card-a7 B11) — on the corner of the avatar of the seat that dealt this deal,
  // yours too, while a deal is played (the old corner chip's span): it moves with every deal, and
  // the seat after it plays first. شلم's marker (`DealerMarker`), never a look of its own.
  const dealerMarkerOn = (seat: number): ReactNode =>
    playing && shown.dealer === seat ? <DealerMarker label={fa.pasur.dealer} /> : null;

  // THE POOL'S ROOM, measured off the felt (see `usePoolRoom`), and its rule (`pasur-pool.ts`).
  const feltRef = useRef<HTMLDivElement>(null);
  const poolBoxRef = useRef<HTMLDivElement>(null);
  const cornersRef = useRef<HTMLDivElement>(null);
  const bottomRowRef = useRef<HTMLDivElement>(null);
  const topSeat = opponents.find((seat) => pasurSeatEdge(seat, you, seats) === "top") ?? null;
  const roomBox = usePoolRoom(
    feltRef,
    poolBoxRef,
    cornersRef,
    bottomRowRef,
    topSeat,
    `${seats}:${playing}:${isYourTurn}`,
  );
  // THE ROOM THE POOL IS LAID OUT IN — the measured one, handed over so that only the POOL's own
  // changes slide its cards (`useLaidRoom`): still under a moment, a shrink at once, a growth (a
  // banner expiring) with the pool's next change — and the slots' transition armed two animation
  // frames after it is laid, again per deal
  // (a DEAL holds no room still: nothing slides away under it — its pool is laid in the room as it is
  // when the deal starts, the first measurement of a mount included)
  const { room, armed: slotsArmed } = useLaidRoom(
    roomBox,
    hold.holding && dealing === null,
    shown.table,
    shown.dealNumber,
  );
  // …and the deal flies out of the stock, re-aimed if the room is laid again while it runs
  const rootRef = useRef<HTMLDivElement>(null);
  usePasurDeal({
    plan,
    id: dealing === null ? 0 : hold.momentId,
    root: rootRef,
    you,
    relayout: room === null ? "" : `${room.top}|${room.bottom}|${room.room.w}|${room.room.h}`,
  });
  // THE POOL AS THE FELT SHOWS IT: the live pool, or the pool a moment's play met — and, for a
  // capture, the card that took the set, laid on it. The set (and that card), or the scoop's
  // remainder, carries `data-sweep`: the hold's collect slides exactly those to the taker's seat.
  const moment = hold.moment;
  const poolCards = shown.table;
  const pool = poolLayout(poolCards.length, room?.room ?? DEFAULT_POOL_ROOM);
  const playedCard = moment?.kind === "capture" ? moment.play : null;
  const sweepingCards = new Set<string>(
    moment === null || moment.kind === "deal" || moment.kind === "breakdown"
      ? []
      : moment.kind === "capture"
        ? moment.play.capture
        : moment.cards,
  );
  const playedAt = ((): { x: number; y: number; tilt: number } | null => {
    if (playedCard === null) return null;
    const anchor = pool.slots[poolCards.findIndex((c) => playedCard.capture.includes(c))];
    if (!anchor) return null;
    const roomW = (room?.room ?? DEFAULT_POOL_ROOM).w;
    const roomH = (room?.room ?? DEFAULT_POOL_ROOM).h;
    const dx = pool.w * PLAYED_NUDGE.x;
    const dy = pool.h * PLAYED_NUDGE.y;
    return {
      x: anchor.x + dx + pool.w <= roomW ? anchor.x + dx : anchor.x - dx,
      y: anchor.y + dy + pool.h <= roomH ? anchor.y + dy : anchor.y - dy,
      tilt: -anchor.tilt,
    };
  })();

  // A moment's banner names its player below 412 px by their first characters (BANNER_NAME_NARROW) —
  // and by fewer where its room is narrower (`bannerLines`, `MomentBanner`: Task 8a fix round 1, F1)
  const bannerNarrow = useMediaQuery(BANNER_NAME_NARROW_QUERY);
  const bannerCap = bannerNarrow ? BANNER_NAME_NARROW : undefined;
  const nameLines = (line: (name: string) => string, seat: number): string[] =>
    bannerLines(line, nameOf(seat), bannerCap);
  // the seats whose columns can reach a banner's row: left and right of you
  const sideSeats = opponents.filter((seat) => {
    const edge = pasurSeatEdge(seat, you, seats);
    return edge === "left" || edge === "right";
  });
  // the newest revealed سور / cancel and scoop, each up for NOTICE_MS from its reveal, once per event
  const surShown = useTransientNotice(hold.sur ? `sur:${hold.sur.id}` : "", NOTICE_MS);
  // a cancel names whose سور it neutralised — or, when the log does not hold it, that SIDE (the side
  // opposite the player who cleared the table), true for whoever reads it: a word, never cut
  const surText = ((): {
    id: "pasur-sur" | "pasur-sur-cancel";
    lines: readonly string[];
  } | null => {
    const sur = hold.sur;
    if (!surShown || sur === null) return null;
    if (sur.kind === "scored")
      return { id: "pasur-sur", lines: nameLines(fa.pasur.surScored, sur.by) };
    // …and last, the nameless line: where not even the name's first letter fits (ruled 2026-10-11)
    if (sur.victim !== null)
      return {
        id: "pasur-sur-cancel",
        lines: [...nameLines(fa.pasur.surCancelled, sur.victim), fa.pasur.surCancelledNameless],
      };
    const side = sideOf(sur.by) === view.yourSide ? fa.pasur.them : fa.pasur.us;
    return { id: "pasur-sur-cancel", lines: [fa.pasur.surCancelled(side)] };
  })();

  // THE سور COUNT, PER SIDE, ON THE SEATS (note 5's «seats»): a seat whose SIDE holds standing سورs
  // this deal wears one chip under its name — your partner yours, both opponents theirs (only one side
  // ever holds any, D10). As the felt shows it: it moves at a سور's reveal, never before the card lands.
  const surChip = (seat: number): ReactNode => {
    const side = sideOf(seat);
    const count = shown.surs[side] ?? 0;
    if (count <= 0) return null;
    return (
      <span
        key={count}
        role="img"
        aria-label={fa.pasur.surCountLabel(
          side === view.yourSide ? fa.pasur.us : fa.pasur.them,
          count,
        )}
        className={SUR_CHIP}
        style={{ animation: "score-bounce .5s ease" }}
      >
        {fa.pasur.surCount(count)}
      </span>
    );
  };

  // Your hand: sorted for display (the shared order), fanned as حکم's and بی‌دل's (`hand-fan.ts`) — as
  // the felt shows it: a refill or a new deal appears once the moment before it is over.
  const handCards = sortHand(shown.hand);
  const n = handCards.length;
  const cardScale = HAND_CARD_SCALE;
  const { step, width: fanWidth } = handFanLayout(n, cardScale);
  // THE FAN REMOUNTS ON EVERY DEAL AND EVERY REFILL ROUND, so the `deal-card` stagger always plays
  // from card 0 — a card id carried across the boundary (the same card dealt again next deal) would
  // otherwise reconcile in place with no entrance. A round is HAND_SIZE plays per seat (the log
  // resets every deal), so the key is stable within a round and an ordinary play re-deals nothing.
  const fanKey = `${shown.dealNumber}:${Math.floor(plays.length / (HAND_SIZE * seats))}`;

  // YOUR STRIP (card-a7 B8): one row or two (`useStripRows`) — its parts, placed by the shape.
  const stripRowRef = useRef<HTMLDivElement>(null);
  const ownRingRef = useRef<HTMLDivElement>(null);
  const stripWorstRef = useRef<HTMLDivElement>(null);
  const stripRows = useStripRows(stripRowRef, ownRingRef, stripWorstRef);
  const stripTwoRows = stripRows.two;
  // `min(168px, 100%)`: a nowrap name's min-content IS its full width, so a plain cap never lets it
  // shrink when the row runs short. `dir="auto"` keeps a Latin name's START.
  const ownNameEl = (
    <span
      dir="auto"
      className={`m-0 inline-block truncate ${compact ? "font-body-lg text-body-lg" : "font-headline-sm text-headline-sm"} text-primary`}
      style={{
        maxWidth: "min(168px, 100%)",
        ...(stripTwoRows ? { gridColumn: 2, gridRow: 1, justifySelf: "start" } : null),
      }}
    >
      {ownName}
    </span>
  );
  // in two rows it holds the slot's height at a solo table too (`min-h-8`: the first row is the
  // online one's, so your strip is ONE height at every table — `frame:strip-height`)
  const statusGroup = (
    <div
      data-strip-status=""
      className={`flex flex-none items-center gap-2${stripTwoRows ? " min-h-8" : ""}`}
      style={stripTwoRows ? { gridColumn: 3, gridRow: 1 } : undefined}
    >
      {pillHidden ? (
        // a flex box of its own: the pill inside keeps its block box (its padding counts)
        <span aria-hidden="true" className="flex invisible">
          <StatusPill gold={isYourTurn}>{statusText}</StatusPill>
        </span>
      ) : (
        <StatusPill gold={yourMove}>{statusText}</StatusPill>
      )}
      <TurnTimerSlot timer={timer} />
    </div>
  );

  return (
    <div
      ref={rootRef}
      className="relative mx-auto flex w-full max-w-[440px] flex-col overflow-hidden text-on-surface select-none"
      style={{
        height: "var(--game-h)",
        background:
          "radial-gradient(circle at center,rgb(var(--c-room-light)) 0%,rgb(var(--c-surface-dim)) 100%)",
      }}
    >
      {/* HUD — ONE 40 px band: the exit, the chat toggle's footprint while online, «پاسور» centred
          in the gap, the score box. Nothing hangs under any of them, so the felt starts right below. */}
      <header className="relative z-40 px-4" style={{ paddingTop: TOP_PAD }}>
        <div className="flex items-center gap-2">
          <button
            type="button"
            aria-label={fa.pasur.leave}
            onClick={onLeave}
            className={`${TABLE_BTN} flex h-10 w-10 flex-none self-start items-center justify-center rounded-full border border-outline-variant bg-surface-container/80 text-on-surface-variant shadow-md transition-transform hover:text-error active:scale-95`}
          >
            <GameIcon type="exit" size={20} />
          </button>
          {chat ? (
            <div
              aria-hidden
              data-testid="chat-toggle-slot"
              className="h-10 flex-none"
              style={{ width: CHAT_TOGGLE_PX }}
            />
          ) : null}
          {/* `min-w-0` + `truncate`: on the narrowest screens the title gives way, never spilling onto
              the box (بی‌دل's measured rule). */}
          <div className="pointer-events-none flex h-10 min-w-0 flex-1 items-center justify-center">
            <h1 className="m-0 max-w-full truncate whitespace-nowrap font-headline-md text-headline-md text-secondary">
              {fa.pasur.title}
            </h1>
          </div>
          {/* «ما» | «حریف» | «تا ۶۲» — each column a FIXED width (SCORE_COL), the box exactly the
              buttons' 40 px (label 16 + numeral 24), the totals re-mounted on change (score-bounce). */}
          <div
            aria-label={fa.pasur.matchScore}
            className="flex h-10 flex-none items-center gap-2.5 rounded-lg border border-outline-variant bg-surface-container/80 px-2.5 shadow-md max-[379px]:gap-1.5 max-[379px]:px-1.5"
          >
            <div
              data-side="us"
              className="flex flex-none flex-col items-center leading-none"
              style={{ width: SCORE_COL.us }}
            >
              <span className="m-0 whitespace-nowrap font-caption text-caption text-on-surface-variant">
                {sideWords.us}
              </span>
              <span
                key={usTotal}
                className="m-0 whitespace-nowrap font-headline-sm text-headline-sm text-primary"
                style={{ animation: "score-bounce .5s ease" }}
              >
                {faDigits(usTotal)}
              </span>
            </div>
            <div className="h-6 w-px flex-none bg-outline-variant" />
            <div
              data-side="them"
              className="flex flex-none flex-col items-center leading-none"
              style={{ width: SCORE_COL.them }}
            >
              <span className="m-0 whitespace-nowrap font-caption text-caption text-on-surface-variant">
                {sideWords.them}
              </span>
              <span
                key={themTotal}
                className="m-0 whitespace-nowrap font-headline-sm text-headline-sm text-error"
                style={{ animation: "score-bounce .5s ease" }}
              >
                {faDigits(themTotal)}
              </span>
            </div>
            <div className="h-6 w-px flex-none bg-outline-variant" />
            <span
              data-side="target"
              className="m-0 flex-none whitespace-nowrap text-center font-caption text-caption text-on-surface-variant"
              style={{ width: SCORE_COL.target }}
            >
              {fa.pasur.target(view.targetScore)}
            </span>
          </div>
        </div>
      </header>

      {/* Felt playfield — with «تأیید قبل از بازی کارت» on, a tap anywhere on it plays the lifted card
          when that card has ONE legal move (its trail, or its lone capture); a card whose choice is
          the player's is confirmed on the card or on «جمع کن», never by the felt (R4, forced
          capture); a tap on a button inside it (a pool card, «جمع کن», the invite «+») stays that
          button's. */}
      <main
        className="relative min-h-0 flex-1"
        onClick={(event) => {
          if (feltPlays(options, liftPlay.lifted, liftToPlay)) liftPlay.tapTable(event);
        }}
      >
        {/* The gold-ringed radial felt (حکم's). */}
        <div
          ref={feltRef}
          className="absolute inset-3 overflow-hidden rounded-[40px]"
          style={FELT_STYLE}
        >
          {/* ONE top row of corner chips: the stock's count while it lasts, else the final round (they
              never coexist: the final round IS the empty stock, D8). Its slot is its own box, so no
              chip reads as another's label. Who dealt is no chip here (card-a7 B11, the operator's
              play-test: «it does nothing and sits there the whole game»): the dealer's marker rides
              that seat's avatar (`DealerMarker`, شلم's), and yours when you dealt. */}
          {playing ? (
            <div
              ref={cornersRef}
              data-felt-corners=""
              className="absolute inset-x-3 top-3 z-20 flex items-center justify-between gap-2"
            >
              <div className="flex min-w-0" style={{ maxWidth: CORNER_SLOT_MAX }}>
                {shown.deckCount > 0 ? (
                  <span aria-label="deck" className={`${CORNER_CHIP} text-on-surface`}>
                    <span className="text-secondary" aria-hidden>
                      <TrickStackIcon size={12} />
                    </span>
                    {plan !== null ? (
                      <DealStockCount key={hold.momentId} plan={plan} />
                    ) : (
                      faDigits(shown.deckCount)
                    )}
                  </span>
                ) : shown.finalRound ? (
                  <FinalRoundChip />
                ) : null}
              </div>
            </div>
          ) : null}

          {/* THE POOL — in the room the felt leaves it (measured: `usePoolRoom`), laid out by
              `pasur-pool.ts`: each card in its slot, tilted, a later row over the one before. Keyed
              by card id: only a NEW pool card mounts (and flies in from its player's edge); a
              capture slides the rest into their new slots. Every pool card is a button — the
              picker's tap target — ELIGIBLE ones glow (`Card`'s legal cue, held steady: the picker
              opens mid-turn), CHOSEN ones ring and count, the rest rest (no glow, never dimmed). While a moment holds, the layer says so
              (`data-holding`, the probes' hook) and draws the pool the moment's play met. */}
          <div
            ref={poolBoxRef}
            className="absolute"
            style={{ ...POOL_SIDE, top: room?.top ?? 0, bottom: room?.bottom ?? 0 }}
          >
            <div
              ref={hold.layerRef}
              aria-label="table"
              data-holding={hold.holding ? "1" : undefined}
              className="relative h-full w-full"
            >
              {poolCards.map((cardId, i) => {
                const slot = pool.slots[i];
                if (!slot) return null;
                const eligible = picker.eligible.has(cardId);
                const chosen = picker.chosen.has(cardId);
                const player = playedBy.get(cardId);
                // dealt by the deal on screen: it comes out of the stock (`use-pasur-deal.ts`)
                const dealt = plan !== null && plan.pool.some((d) => d.card === cardId);
                return (
                  <span
                    key={cardId}
                    data-eligible={eligible ? "1" : undefined}
                    data-chosen={chosen ? "1" : undefined}
                    data-deal-pool={dealt ? cardId : undefined}
                    data-enter-from={dealt ? "stock" : undefined}
                    className="absolute inline-flex"
                    style={{
                      left: slot.x,
                      top: slot.y,
                      width: pool.w,
                      height: pool.h,
                      transform: `rotate(${slot.tilt}deg)`,
                      // a re-flow slides the slots — but not a MEASUREMENT: the layout effect
                      // reads the room before the first paint, and a transition armed when the
                      // browser first styles the real positions runs from the fallback ones (a
                      // transition is defined by the AFTER-change style) — sixteen cards sliding
                      // under the fly-in at 384 / 390 (124 → 272 px tall). So it is armed two
                      // animation frames after the room is laid, per deal; and a room that grows
                      // under cards lying still waits for the pool's next change (`useLaidRoom`).
                      transition: slotsArmed
                        ? "left .25s ease, top .25s ease, transform .15s ease"
                        : "none",
                    }}
                  >
                    {/* THE PAINTED CARD IS THE SLOT, in this RTL shell too. The flight's root fills
                        the slot, and the scale rides a wrapper of its own (the flight animates the
                        ThrownCard's transform and would drop a scale set there) that is OUT OF FLOW
                        at the slot's physical top-left with the card's own unscaled box: in flow, a
                        child wider than its inline-flex slot sits at the inline END and paints
                        44·(1 − scale) px to the LEFT of the slot — 3.6 px at 0.92, the Task 4 boxes
                        run at 320×568 ×1.3 (a chosen card's ring then crossed the left seat's name);
                        an absolute box with no width of its own shrinks to the slot's width and
                        overflows the same way. */}
                    {/* The collect's own wrapper (no resting transform of its own: the slot keeps
                        the place and the tilt, the flight its entrance): a card a moment takes
                        away — `data-sweep` — slides with it to the taker's seat. */}
                    <span
                      data-sweep={sweepingCards.has(cardId) ? "1" : undefined}
                      data-tilt={slot.tilt}
                      className="absolute inset-0"
                    >
                      {/* a PLAYED card flies in from its player's seat; a DEALT one never from an
                          edge — the deal on screen brings it out of the stock (Task 6), and one a
                          mount adopted (a resume) is simply there */}
                      <ThrownCard
                        className="absolute inset-0"
                        edge={player === undefined ? null : pasurSeatEdge(player, you, seats)}
                      >
                        <span
                          className="absolute left-0 top-0 inline-flex"
                          style={{
                            width: POOL_CARD.w,
                            height: POOL_CARD.h,
                            transform: `scale(${pool.scale})`,
                            transformOrigin: "top left",
                          }}
                        >
                          <Card
                            id={cardId}
                            silent
                            resting={!eligible}
                            disabled={yourMove && choosing && !eligible}
                            selected={chosen}
                            // offered only after your lift, mid-turn: its cue holds steady, no pulse
                            steadyCue
                            onClick={() => tapPool(cardId)}
                          />
                        </span>
                      </ThrownCard>
                    </span>
                    {chosen ? (
                      <span aria-hidden="true" className={COUNT_BADGE}>
                        {faDigits(picker.chosen.size)}
                      </span>
                    ) : null}
                  </span>
                );
              })}
              {/* THE CARD THAT TOOK THE SET, on it: flying in from its player's seat, then holding,
                  then sliding with the set (a capture's played card never joins the pool). */}
              {playedCard !== null && playedAt !== null ? (
                <span
                  key={`played:${playedCard.card}`}
                  data-played="1"
                  className="absolute inline-flex"
                  style={{
                    left: playedAt.x,
                    top: playedAt.y,
                    width: pool.w,
                    height: pool.h,
                    transform: `rotate(${playedAt.tilt}deg)`,
                    zIndex: poolCards.length + 1,
                  }}
                >
                  <span data-sweep="1" data-tilt={playedAt.tilt} className="absolute inset-0">
                    <ThrownCard
                      className="absolute inset-0"
                      edge={pasurSeatEdge(playedCard.seat, you, seats)}
                    >
                      <span
                        className="absolute left-0 top-0 inline-flex"
                        style={{
                          width: POOL_CARD.w,
                          height: POOL_CARD.h,
                          transform: `scale(${pool.scale})`,
                          transformOrigin: "top left",
                        }}
                      >
                        <Card id={playedCard.card} />
                      </span>
                    </ThrownCard>
                  </span>
                </span>
              ) : null}
            </div>
          </div>

          {/* THE FELT'S NOTICES COLUMN (بی‌دل's `felt-notices`), at its bottom, where no seat reaches
              and the pool stops short of — the room is measured to its top. `flex-col-reverse`: its
              FIRST child is its BOTTOM one —
                the row      ONE row, one height on every screen and every turn (the strut): your
                             prompt and the picker's «جمع کن», on YOUR screen, on your move, never on
                             anybody else's and never while a capture holds (the prompt says play,
                             or asks which set; the pill plays the chosen capture, greyed until one is
                             chosen — card-91's (b));
                the moments  above it, each in the one BANNER_PILL, up NOTICE_MS from its reveal: a
                             سور «سور! — {name}», its cancel «سور {name} خنثی شد» (one of the two at a
                             time: the newer replaces the older), the scoop «باقی زمین به {name}». */}
          <div
            ref={bottomRowRef}
            data-testid="felt-notices"
            className="pointer-events-none absolute inset-x-0 bottom-3 z-20 flex flex-col-reverse items-center gap-1 px-4"
          >
            <div
              ref={feltRowRef}
              data-felt-row=""
              className="relative flex w-full flex-wrap items-center justify-center gap-2 gap-y-1"
            >
              <span aria-hidden="true" className={FELT_STRUT}>
                {"\u200b"}
              </span>
              {yourMove && choosing ? (
                // the question and the chips when the row holds them, the chips alone where it does
                // not (a narrow screen, a big text zoom: `setsFit`), and when even the tightest
                // form is wider than the row they WRAP — whatever the pointer (operator 2026-10-21:
                // «wrap the «کدام دسته؟» buttons onto a second line instead of scrolling»; the B6
                // review: a touchscreen laptop still scrolled) — and the pool's room, measured off
                // this row (`usePoolRoom`), shrinks with it. Never a scroll.
                <div
                  data-testid="pasur-prompt"
                  role="group"
                  aria-label={fa.pasur.pickSet}
                  className="pointer-events-auto m-0 flex min-w-0 items-center justify-center"
                >
                  <SetChips
                    sets={picker.sets}
                    selectedKey={picker.selectedKey}
                    question={picker.prompt === "pick" && setsFit === 0}
                    numbers={setsFit >= 2}
                    tight={setsFit >= 3}
                    wrap
                    onPick={chooseSet}
                  />
                </div>
              ) : null}
              {yourMove && choosing ? (
                // the forms' measuring copies: invisible, out of flow, never named
                <>
                  {SETS_FORMS.map((form) => (
                    <span
                      key={`${form.question}|${form.numbers}|${form.tight}`}
                      aria-hidden="true"
                      data-sets-form=""
                      className="pointer-events-none invisible absolute flex w-max items-center gap-2"
                    >
                      <SetChips
                        sets={picker.sets}
                        selectedKey={null}
                        question={form.question}
                        numbers={form.numbers}
                        tight={form.tight}
                        onPick={null}
                      />
                      <span
                        data-copy-text={fa.pasur.confirm}
                        className={`${FELT_PILL} whitespace-nowrap ${COPY_TEXT}`}
                      />
                    </span>
                  ))}
                </>
              ) : null}
              {yourMove && picker.prompt !== null && !choosing ? (
                <span
                  data-testid="pasur-prompt"
                  className={`${FELT_PILL} border-secondary/50 bg-surface-container/90 text-secondary animate-pulse`}
                >
                  {picker.prompt === "pick" ? fa.pasur.pickSet : fa.pasur.prompt}
                </span>
              ) : null}
              {yourMove && picker.confirm !== null ? (
                <button
                  key={picker.lifted}
                  type="button"
                  data-testid="pasur-confirm"
                  disabled={picker.confirm !== "ready"}
                  aria-label={
                    picker.confirm === "ready"
                      ? fa.pasur.confirmTakes(picker.chosen.size)
                      : fa.pasur.confirmWaiting
                  }
                  onClick={() => liftPlay.play()}
                  // `TABLE_BTN`'s reset without its `cursor-pointer` (a later utility would beat the
                  // waiting pill's `cursor-default` — the same stylesheet-order rule as the strut's)
                  className={`appearance-none m-0 ${FELT_PILL} pointer-events-auto whitespace-nowrap ${
                    picker.confirm === "ready"
                      ? "cursor-pointer border-secondary/70 bg-gradient-to-br from-secondary to-gold-deep text-ink-on-gold active:scale-95"
                      : "cursor-default border-outline-variant bg-surface-container/70 text-on-surface-variant opacity-60"
                  }`}
                  style={{ animation: "bg-slidein .25s ease-out" }}
                >
                  {fa.pasur.confirm}
                </button>
              ) : null}
            </div>
            {surText !== null ? (
              <MomentBanner
                id={surText.id}
                lines={surText.lines}
                felt={feltRef}
                sides={sideSeats}
              />
            ) : null}
            {/* the scoop's label while the remainder slides — the deal's card carries it after */}
            {moment?.kind === "scoop" && hold.revealed && moment.seat !== null ? (
              <MomentBanner
                id="pasur-scoop"
                lines={nameLines(fa.pasur.scoopTo, moment.seat)}
                felt={feltRef}
                sides={sideSeats}
              />
            ) : null}
          </div>
        </div>

        {opponents.map((seat) => {
          const edge = pasurSeatEdge(seat, you, seats);
          if (edge === "bottom") return null;
          const mine = sideOf(seat) === view.yourSide;
          const top = edge === "top";
          return (
            <PasurSeat
              key={seat}
              seat={seat}
              posClass={SEAT_POS[edge]}
              avatar={avatarOf(seat)}
              name={nameOf(seat)}
              notice={noticeForSeat(seatNotices, seat)}
              onTurn={playing && dealing === null && breakdown === null && shown.turn === seat}
              compact={compact}
              onInvite={inviteFor(seat)}
              ringTone={teamRing(mine)}
              aboveAvatar={mine ? <PartnerTag /> : null}
              nameSkin={seatNameSkin(mine)}
              nameMax={top ? TOP_NAME_MAX : SIDE_NAME_MAX}
              wrapNotice={!top}
              extra={surChip(seat)}
              avatarCorner={dealerMarkerOn(seat)}
            />
          );
        })}

        {/* THE DEAL'S LAYER (Task 6) — only while a deal is on screen: the other seats' cards on their
            way out of the stock, face down, and a buried Jack's stay on the table (D6) — each placed
            and flown by `use-pasur-deal.ts`, invisible at rest. Over the felt and the seats; it takes
            no tap. */}
        {plan !== null ? (
          <div
            data-deal-layer=""
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 z-[25]"
          >
            {plan.seats
              .filter((p) => p.seat !== you)
              .flatMap((p) =>
                Array.from({ length: HAND_SIZE }, (_u, j) => (
                  <span
                    key={`${p.seat}:${j}`}
                    data-deal-back={`${p.seat}:${j}`}
                    className="absolute"
                    style={DEAL_BACK_STYLE}
                  >
                    <Card id="back" faceDown small />
                  </span>
                )),
              )}
            {plan.buried.map((b, j) => (
              <span
                key={`bury:${j}`}
                data-deal-bury={j}
                className="absolute"
                style={{
                  left: 0,
                  top: 0,
                  width: pool.w,
                  height: pool.h,
                  marginLeft: -pool.w / 2,
                  marginTop: -pool.h / 2,
                  opacity: 0,
                }}
              >
                <span
                  className="absolute left-0 top-0 inline-flex"
                  style={{
                    width: POOL_CARD.w,
                    height: POOL_CARD.h,
                    transform: `scale(${pool.scale})`,
                    transformOrigin: "top left",
                  }}
                >
                  <Card id={b.card} silent />
                </span>
              </span>
            ))}
          </div>
        ) : null}

        {/* THE DEAL'S CARD (Task 7): over the felt's own rect, above the seats, for its whole span, in
            the richest form its room holds (Task 8a — `BreakdownLayer`). */}
        {breakdown !== null ? (
          <BreakdownLayer
            deal={breakdown.deal}
            yourSide={view.yourSide}
            seats={seats}
            target={view.targetScore}
            scoopLines={
              breakdown.deal.scoop === null
                ? null
                : nameLines(fa.pasur.scoopTo, breakdown.deal.scoop.seat)
            }
          />
        ) : null}
      </main>

      {/* Your panel: the strip, then your hand. */}
      {/* Over two rows (`useStripRows`, card-a7 B8) the second row's height comes out of the top
          padding (`pad`): the panel is the height it was, the felt not moved by a pixel. */}
      <div
        className="relative z-30 flex flex-col gap-2 bg-gradient-to-t from-surface-container-lowest via-surface-container-lowest/80 to-transparent px-3 pt-2"
        style={{
          paddingBottom: "calc(14px + var(--game-safe-bottom))",
          ...(stripTwoRows ? { paddingTop: stripRows.pad ?? PANEL_PAD_TOP } : null),
        }}
      >
        {/* Your strip — avatar → name (the last deal under it) → [the status pill → the turn timer's
            ONE fixed slot, LAST: the strip's physical left end under rtl]. `min-w-0` down the name's
            side lets a long name truncate; the other group never shrinks and its pill never wraps.
            The name's side FILLS the rest (`flex-1`, its column too), so the last deal's line has
            the whole room to measure, whatever it shows (`useRichestFit`); its contents still sit
            at the start, beside your ring.
            A height FLOOR — the tallest the row draws at 100 %: your name (24) over the last deal's
            line (2 + 16) = 42, your own ring 42 — a floor, not a fixed box, so a WebView text zoom
            grows it instead of spilling (بی‌دل's, review 6b F1); the line's twin keeps it in every
            phase. */}
        <div
          ref={stripRowRef}
          data-strip-rows={stripTwoRows ? 2 : 1}
          className={
            stripTwoRows
              ? "relative grid min-h-[42px] items-center gap-x-2"
              : "relative flex min-h-[42px] items-center justify-between gap-2"
          }
          style={stripTwoRows ? { gridTemplateColumns: STRIP_TWO_ROW_COLUMNS } : undefined}
        >
          {/* TWO ROWS (`useStripRows`, card-a7 B8) — the same parts, placed on a grid: your ring
              over both rows, your name and the status group on the first, the tallies' line the
              width of both under them; the ring's group and the name's column draw no box of their
              own there (`contents`). Your ring's track gives way first, as its group does in one
              row, so the slot stays at the strip's end. */}
          <div className={stripTwoRows ? "contents" : "flex min-w-0 flex-1 items-center gap-2"}>
            <div
              ref={ownRingRef}
              className="relative flex items-center justify-center rounded-full"
              style={{
                padding: 2,
                boxSizing: "border-box",
                boxShadow: yourMove ? TURN_GLOW : OWN_RING,
                animation: yourMove ? TURN_GLOW_SETTLE : undefined,
                ...(stripTwoRows ? { gridColumn: 1, gridRow: "1 / span 2" } : null),
              }}
            >
              <Avatar
                avatarId={ownAvatar.avatarId}
                avatarPhotoId={ownAvatar.avatarPhotoId}
                size={compact ? OWN_AVATAR.compact : OWN_AVATAR.regular}
                official={ownAvatar.official}
              />
              {dealerMarkerOn(you) ? (
                <div className={AVATAR_CORNER}>{dealerMarkerOn(you)}</div>
              ) : null}
            </div>
            <div
              className={
                stripTwoRows
                  ? "contents leading-tight"
                  : "flex min-w-0 flex-1 flex-col items-start gap-0.5 leading-tight"
              }
            >
              {ownNameEl}
              <TalliesLine
                view={shown}
                style={stripTwoRows ? { gridColumn: "2 / span 2", gridRow: 2 } : undefined}
              />
            </div>
          </div>
          {statusGroup}
          <StripWorst view={shown} copiesRef={stripWorstRef} />
        </div>

        {/* Your hand — sorted, fanned, only the server's legal plays tappable (risen + the gold
            pulse); the rest stay UNDIMMED (Card's entrance rule — `entered`: each card's entrance is
            the deal's flight out of the stock, `data-deal-hand`, Task 6). Keyed per deal and refill
            round.
            A card that LIFTS (`liftable`) is silent and the hook's; while one is lifted it stands
            LIFTED_EXTRA_PX over the legal rise and the other legal cards REST at 0, pulse-less (حکم's
            and بی‌دل's rule) — still tappable: a tap switches, or plays a one-move card at once.
            Every send goes through the hook's once-per-frame latch and its one «play» cue. */}
        <div
          key={fanKey}
          ref={handRef}
          aria-label="your hand"
          className="flex w-full justify-center overflow-visible px-2"
        >
          <div
            className="relative"
            style={{ height: HAND_FAN_BOX_H, width: fanWidth, maxWidth: "100%" }}
          >
            {handCards.map((card, i) => {
              // the server's legal plays, AND-ed with no capture holding (حکم's and بی‌دل's rule,
              // Part B §4.6): while one does, no card rises, pulses or plays
              const canPlay = options.has(card) && !hold.holding;
              const isLifted = liftPlay.lifted === card;
              // a legal card rides 18 px over the arc (حکم's and بی‌دل's legal rise)
              const rise = isLifted
                ? 18 + LIFTED_EXTRA_PX
                : canPlay && liftPlay.lifted === null
                  ? 18
                  : 0;
              return (
                <div
                  key={card}
                  data-deal-hand={card}
                  className="absolute bottom-0"
                  style={{
                    // its place on the fan's arc, `rise` px over it (`hand-fan.ts`)
                    ...handCardPlacement(i, n, step, rise, cardScale),
                    zIndex: i,
                    // the lifted card rises on an easing that never overshoots (LIFTED_TRANSITION)
                    transition: isLifted
                      ? LIFTED_TRANSITION
                      : "transform .2s cubic-bezier(.175,.885,.32,1.275)",
                  }}
                >
                  {liftable.has(card) ? (
                    <Card
                      id={card}
                      large
                      entered
                      disabled={!canPlay}
                      silent
                      selected={isLifted}
                      lifted={isLifted}
                      resting={liftPlay.lifted !== null && !isLifted}
                      onClick={() => {
                        const tap = liftPlay.tapCard(card);
                        if (tap === "nudge") nudgeLifted();
                        // a put-back and a SWITCH drop the choice with the lift (as a new frame
                        // does): the re-lifted card asks again — switching away and back must not
                        // resurrect a set the player left (Task 4 review, Important 2)
                        else if (tap === "putBack" || tap === "switch") setPick(null);
                      }}
                    />
                  ) : (
                    <Card
                      id={card}
                      large
                      entered
                      disabled={!canPlay}
                      silent
                      resting={liftPlay.lifted !== null}
                      onClick={() => liftPlay.send(card)}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
